import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getCharacter,
  deleteCharacter,
  canManageCharacter,
  getLatestWeeklyBoard,
  getTasks,
  getTaskOutputs,
  getVideoAssets,
  getAudioAssets,
  getMinimaxVoices,
  countWeeklyBoards,
  getGenCount,
  getCharacterProfile,
  generateAvatar,
  uploadAvatar,
  generateStrategy,
  generateContent,
  generateVideoImage,
  generateVideoMinimax,
  generateVideoSelfie,
  generateVoice,
  genTextToImage,
  genWardrobeLockface,
  generateStoryboard,
  generateVideoAgent,
  getHeygenVoices,
  setHeygenVoice,
  setAvatar,
  getCharacterPhotos,
  getPendingAssets,
  saveCharacterPhoto,
  confirmAsset,
  rejectAsset,
  setTaskFlag,
  getRolePlaybook,
  generateRolePlaybook,
  getTaskMetricResults,
  saveTaskMetricResults,
  type DbCharacter,
  type DbCharacterProfile,
  type DbWeeklyBoard,
  type DbTask,
  type DbTaskOutput,
  type DbAsset,
  type DbMinimaxVoice,
  type DbRolePlaybook,
  type RolePlaybookData,
  type Storyboard,
  type DbHeygenVoice,
} from '@/api/astraea';
import { spendCredits, spendCreditsUnits, getCreditCosts, isAdmin, getUserId, spendWardrobeLockface, getWardrobeFreeRemaining } from '@/api/auth';
import { DEFAULT_PORTRAIT_ASPECT, wrapWardrobePrompt } from '@/lib/imagePrompts';
import ProfileSummary from '@/components/ProfileSummary';
import ConfirmDialog from '@/components/ConfirmDialog';
import { usePaidGenerationGuard } from '@/hooks/usePaidGenerationGuard';
import { PAID_GEN_ABANDON_MESSAGE, PAID_GEN_ABANDON_TITLE, PAID_GEN_ABANDON_WARDROBE_MESSAGE } from '@/lib/paidGeneration';
import MinimaxVoiceSelect from '@/components/MinimaxVoiceSelect';
import HeygenVoiceSelect from '@/components/HeygenVoiceSelect';
import { WardrobePreview, CharacterPhotoGrid, PendingAssetCard } from '@/components/CharacterPhotoLibrary';
import { normalizeVoiceGender } from '@/lib/voiceGroups';
import { getTodayDecision } from '@/api/commercialPipeline';
import type { ManagerExplanation } from '@/lib/commercial/explainTypes';
import type { ConversionPlaybook } from '@/lib/commercial/conversionPlaybookTypes';
import { conversionPlaybookFromDecision } from '@/lib/commercial/conversionPlaybook';
import TodayBriefingCard from '@/components/TodayBriefingCard';
import ConversionPlaybookCard from '@/components/ConversionPlaybookCard';

// 影片(HeyGen 口播)按时长计费:讲稿越长越贵。以「每 30 秒≈120 字」为一计价单位。
const unitsForChars = (n: number) => Math.max(1, Math.ceil((n || 0) / 120));
import { buildTaskBoard, type BoardTask, type TaskStatus } from '@/api/taskBoard';

const platformLabel: Record<string, string> = { xiaohongshu: '小红书', douyin: '抖音', kuaishou: '快手', bilibili: 'B站', wechat_channels: '微信视频号', zhihu: '知乎', wb58: '58同城' };
const pLabel = (p: string) => platformLabel[p] || p;

const RISK: Record<string, { label: string; color: string; dot: string }> = {
  low: { label: '可安全发布', color: 'bg-green-50 text-green-700', dot: 'bg-green-500' },
  medium: { label: '中度风险，建议检查', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  high: { label: '高风险，别用此帐号直接发', color: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
};

const TASK_STATUS_BADGE: Record<TaskStatus, { label: string; cls: string; dot: string }> = {
  pending: { label: '待生成', cls: 'bg-secondary-100 text-secondary-500', dot: 'bg-secondary-400' },
  generating: { label: '生成中', cls: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  generated: { label: '已生成', cls: 'bg-primary-50 text-primary-700', dot: 'bg-primary-500' },
  needs_review: { label: '待检查', cls: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  completed: { label: '已完成', cls: 'bg-green-50 text-green-700', dot: 'bg-green-500' },
  failed: { label: '生成失败', cls: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
};

type DetailTab = 'exec' | 'content' | 'pd' | 'asset' | 'metrics' | 'detail';
const TABS: { key: DetailTab; label: string; icon: string }[] = [
  { key: 'exec', label: '执行指令', icon: 'ri-list-check-2' },
  { key: 'content', label: '生成内容', icon: 'ri-quill-pen-line' },
  { key: 'pd', label: '私域承接', icon: 'ri-chat-3-line' },
  { key: 'asset', label: '素材', icon: 'ri-image-2-line' },
  { key: 'metrics', label: '验收', icon: 'ri-bar-chart-2-line' },
  { key: 'detail', label: '详细策略', icon: 'ri-information-line' },
];

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function downloadFromUrl(filename: string, url: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u; a.download = filename; a.click();
    URL.revokeObjectURL(u);
  } catch {
    window.open(url, '_blank');
  }
}

type StoredJob = { id: string; label: string; taskId?: number; tab?: DetailTab; startedAt: number };
const JOB_TTL_MS = 15 * 60 * 1000;

function readStoredJobs(key: string): StoredJob[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const now = Date.now();
    return (JSON.parse(raw) as StoredJob[]).filter((j) => now - j.startedAt < JOB_TTL_MS);
  } catch { return []; }
}

function writeStoredJobs(key: string, jobs: StoredJob[]) {
  try { localStorage.setItem(key, JSON.stringify(jobs)); } catch { /* ignore */ }
}

function addStoredJob(key: string, job: StoredJob) {
  writeStoredJobs(key, [...readStoredJobs(key).filter((j) => j.id !== job.id), job]);
}

function removeStoredJob(key: string, id: string) {
  writeStoredJobs(key, readStoredJobs(key).filter((j) => j.id !== id));
}

export default function BoardPage() {
  const { characterId } = useParams();
  const nav = useNavigate();
  const cid = Number(characterId ?? 1);

  const [admin, setAdmin] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [character, setCharacter] = useState<DbCharacter | null>(null);
  const [board, setBoard] = useState<DbWeeklyBoard | null>(null);
  const [tasks, setTasks] = useState<DbTask[]>([]);
  const [outputs, setOutputs] = useState<Record<number, DbTaskOutput>>({});
  const [videos, setVideos] = useState<DbAsset[]>([]);
  const [audios, setAudios] = useState<DbAsset[]>([]);
  const [voices, setVoices] = useState<DbMinimaxVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [voiceBusyTaskId, setVoiceBusyTaskId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingTaskId, setGeneratingTaskId] = useState<number | null>(null);
  const [videoBusy, setVideoBusy] = useState<{ taskId: number; mode: 'image_video' | 'minimax_video' | 'selfie_talk' } | null>(null);
  const [coverBusyTaskId, setCoverBusyTaskId] = useState<number | null>(null);
  const [coverImages, setCoverImages] = useState<Record<number, { url?: string }>>({});
  const [info, setInfo] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [videoNote, setVideoNote] = useState<{ taskId: number; tone: 'info' | 'error'; text: string } | null>(null);
  const [phase, setPhase] = useState<number>(1);
  const [costs, setCosts] = useState<Record<string, number>>({});
  const [profile, setProfile] = useState<DbCharacterProfile | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [uploadConsent, setUploadConsent] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement | null>(null);
  // 任务驱动 UI 状态
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('exec');
  const [showFullStrategy, setShowFullStrategy] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showWeeklyDetail, setShowWeeklyDetail] = useState(false);
  const [playbook, setPlaybook] = useState<DbRolePlaybook | null>(null);
  const [playbookBusy, setPlaybookBusy] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  // 模特换衣:先生成预览,用户确认脸相似后才存入角色相册
  const [showWardrobe, setShowWardrobe] = useState(false);
  const [wardrobePrompt, setWardrobePrompt] = useState('');
  const [wardrobeBusy, setWardrobeBusy] = useState(false);
  const [wardrobePreview, setWardrobePreview] = useState<{ url: string; prompt: string } | null>(null);
  const [wardrobeFreeRemaining, setWardrobeFreeRemaining] = useState(0);
  const [characterPhotos, setCharacterPhotos] = useState<DbAsset[]>([]);
  const [pendingAssets, setPendingAssets] = useState<DbAsset[]>([]);
  const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
  const [assetConfirmBusy, setAssetConfirmBusy] = useState(false);
  const [libraryAvatarBusy, setLibraryAvatarBusy] = useState(false);
  // 验收回填(写 DB):metricResults[taskId][metric_key] = value
  const [metricResults, setMetricResults] = useState<Record<number, Record<string, string>>>({});
  const [metricsSavingTaskId, setMetricsSavingTaskId] = useState<number | null>(null);
  // AI 分镜短影音:分镜草稿(免费)→ 确认渲染(扣点)
  const [storyboards, setStoryboards] = useState<Record<number, Storyboard>>({});
  const [sbBusyTaskId, setSbBusyTaskId] = useState<number | null>(null);
  const [agentBusyTaskId, setAgentBusyTaskId] = useState<number | null>(null);
  const [heygenVoices, setHeygenVoices] = useState<DbHeygenVoice[]>([]);
  const [todayExplanation, setTodayExplanation] = useState<ManagerExplanation | null>(null);
  const [todayConversionSop, setTodayConversionSop] = useState<ConversionPlaybook | null>(null);

  const STRAT_KEY = useMemo(() => `astraea_gen_strategy_${cid}`, [cid]);
  const UI_STATE_KEY = useMemo(() => `astraea_board_ui_${cid}`, [cid]);
  const JOBS_KEY = useMemo(() => `astraea_board_jobs_${cid}`, [cid]);
  const WARDROBE_KEY = useMemo(() => `astraea_wardrobe_preview_${cid}`, [cid]);
  const [strategyPending, setStrategyPending] = useState<boolean>(() => !!localStorage.getItem(STRAT_KEY));
  const [uiRestored, setUiRestored] = useState(false);
  const [storedJobs, setStoredJobs] = useState<StoredJob[]>(() => readStoredJobs(`astraea_board_jobs_${cid}`));
  const [confirmReq, setConfirmReq] = useState<{ title: string; message: string; confirmLabel?: string; cancelLabel?: string; resolve: (ok: boolean) => void } | null>(null);

  useEffect(() => {
    isAdmin().then(setAdmin).catch(() => setAdmin(false));
    getUserId().then(setUid).catch(() => setUid(null));
  }, []);

  const canManage = useMemo(
    () => (character ? canManageCharacter(character, uid, admin) : false),
    [character, uid, admin],
  );
  const roleVoiceGender = useMemo<'female' | 'male' | null>(() => {
    if (character?.gender === 'female' || character?.gender === 'male') return character.gender;
    return null;
  }, [character?.gender]);
  const roleVoices = useMemo(
    () => (roleVoiceGender ? voices.filter((v) => normalizeVoiceGender(v.gender) === roleVoiceGender) : voices),
    [voices, roleVoiceGender],
  );
  const adminManagingOther = admin && !!character?.owner_id && character.owner_id !== uid;

  const askConfirm = useCallback((title: string, message: string, opts?: { confirmLabel?: string; cancelLabel?: string }) => (
    new Promise<boolean>((resolve) => { setConfirmReq({ title, message, confirmLabel: opts?.confirmLabel, cancelLabel: opts?.cancelLabel, resolve }); })
  ), []);

  const handleDeleteCharacter = async () => {
    if (!character || !canManage) return;
    const ok = await askConfirm('删除角色', `确定删除「${character.name}」？策略、任务与素材将一并删除,已花费的点数不会退回。`, { confirmLabel: '删除' });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteCharacter(character.id);
      nav(adminManagingOther ? '/admin' : '/board');
    } catch (e) {
      setError(e instanceof Error ? e.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const trackJob = useCallback((job: StoredJob) => {
    addStoredJob(JOBS_KEY, job);
    setStoredJobs(readStoredJobs(JOBS_KEY));
  }, [JOBS_KEY]);

  const clearJob = useCallback((id: string) => {
    removeStoredJob(JOBS_KEY, id);
    setStoredJobs(readStoredJobs(JOBS_KEY));
  }, [JOBS_KEY]);

  // 恢复上次浏览位置(滑走/刷新后仍回到同一任务与分页)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(UI_STATE_KEY);
      if (!raw) { setUiRestored(true); return; }
      const s = JSON.parse(raw) as { selectedTaskId?: number; activeTab?: DetailTab };
      if (s.selectedTaskId) setSelectedTaskId(s.selectedTaskId);
      if (s.activeTab) setActiveTab(s.activeTab);
    } catch { /* ignore */ }
    setUiRestored(true);
  }, [UI_STATE_KEY]);

  useEffect(() => {
    if (!uiRestored) return;
    try {
      sessionStorage.setItem(UI_STATE_KEY, JSON.stringify({ selectedTaskId, activeTab }));
    } catch { /* ignore */ }
  }, [UI_STATE_KEY, selectedTaskId, activeTab, uiRestored]);

  // 换装预览:刷新后仍保留,直到用户确认或放弃
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(WARDROBE_KEY);
      if (raw) setWardrobePreview(JSON.parse(raw) as { url: string; prompt: string });
    } catch { /* ignore */ }
  }, [WARDROBE_KEY]);

  useEffect(() => {
    try {
      if (wardrobePreview) sessionStorage.setItem(WARDROBE_KEY, JSON.stringify(wardrobePreview));
      else sessionStorage.removeItem(WARDROBE_KEY);
    } catch { /* ignore */ }
  }, [wardrobePreview, WARDROBE_KEY]);

  // 封面图(独立生成链 text_to_image,不进任务 assets)用 localStorage 暂存,重整后仍可见
  const coverKey = useCallback((taskId: number) => `astraea_cover_${cid}_${taskId}`, [cid]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const ch = await getCharacter(cid);
      setCharacter(ch);
      getCharacterProfile(cid).then(setProfile).catch(() => {});
      getRolePlaybook(cid).then(setPlaybook).catch(() => {});
      const wb = await getLatestWeeklyBoard(cid);
      setBoard(wb);
      countWeeklyBoards(cid).then((n) => setPhase(Math.max(1, n))).catch(() => {});
      try {
        const raw = localStorage.getItem(STRAT_KEY);
        if (raw) {
          const m = JSON.parse(raw) as { at: number; prevId: number };
          if (wb && wb.id > (m.prevId || 0)) { localStorage.removeItem(STRAT_KEY); setStrategyPending(false); }
          else if (Date.now() - (m.at || 0) > 4 * 60 * 1000) { localStorage.removeItem(STRAT_KEY); setStrategyPending(false); }
          else { setStrategyPending(true); }
        }
      } catch { /* ignore */ }
      const ts = wb ? await getTasks(wb.id) : [];
      setTasks(ts);
      const outs = await getTaskOutputs(ts.map((t) => t.id));
      const byTask: Record<number, DbTaskOutput> = {};
      for (const o of outs) if (!byTask[o.task_id]) byTask[o.task_id] = o;
      setOutputs(byTask);
      const videoList = await getVideoAssets(cid);
      const audioList = await getAudioAssets(cid);
      setVideos(videoList);
      setAudios(audioList);
      setCharacterPhotos(await getCharacterPhotos(cid));
      setPendingAssets(await getPendingAssets(cid));
      if (ch?.avatar_url) {
        getWardrobeFreeRemaining(cid).then(setWardrobeFreeRemaining).catch(() => setWardrobeFreeRemaining(2));
      } else setWardrobeFreeRemaining(0);
      // 验收回填(从 DB 读,重进页面仍在,不依赖 localStorage)
      if (wb) {
        getTaskMetricResults(wb.id).then((mrs) => {
          const map: Record<number, Record<string, string>> = {};
          for (const m of mrs) { (map[m.task_id] ||= {})[m.metric_key] = m.metric_value ?? ''; }
          setMetricResults(map);
        }).catch(() => {});
      }
      // 还原封面图暂存
      const covers: Record<number, { url?: string }> = {};
      for (const t of ts) { const u = localStorage.getItem(coverKey(t.id)); if (u) covers[t.id] = { url: u }; }
      setCoverImages(covers);
      // 清除已完成的进行中任务标记(刷新后仍看得到,完成自动消失)
      const alive = readStoredJobs(JOBS_KEY).filter((j) => {
        if (j.id.startsWith('content-') && j.taskId) return !byTask[j.taskId];
        if (j.id.startsWith('voice-') && j.taskId) return !audioList.some((a) => a.task_id === j.taskId);
        if (j.id.startsWith('cover-') && j.taskId) return !covers[j.taskId]?.url;
        if (j.id.startsWith('video-') && j.taskId) return videoList.some((v) => v.task_id === j.taskId && v.status === 'generating');
        return true;
      });
      writeStoredJobs(JOBS_KEY, alive);
      setStoredJobs(alive);
      // Explain + SOP（失败安静隐藏；与素材页同源函数）
      getTodayDecision(cid)
        .then((d) => {
          setTodayExplanation(d.explanation ?? null);
          setTodayConversionSop(
            conversionPlaybookFromDecision(d, {
              name: ch?.name,
              description: ch?.description ?? undefined,
            }),
          );
        })
        .catch(() => {
          setTodayExplanation(null);
          setTodayConversionSop(null);
        });
    } catch (e) {
      setError(e instanceof Error ? e.message : '读取失败');
    } finally {
      setLoading(false);
    }
  }, [cid, STRAT_KEY, coverKey, JOBS_KEY]);

  useEffect(() => {
    let cancelled = false;
    getMinimaxVoices().then((vs) => { if (cancelled) return; setVoices(vs); }).catch(() => {});
    getCreditCosts().then((cs) => { if (!cancelled) { const m: Record<string, number> = {}; for (const c of cs) m[c.action] = c.cost; setCosts(m); } }).catch(() => {});
    getHeygenVoices().then((vs) => { if (!cancelled) setHeygenVoices(vs); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (roleVoices.length === 0) return;
    setSelectedVoice((prev) => (roleVoices.some((v) => v.voice_id === prev) ? prev : roleVoices[0].voice_id));
  }, [roleVoices]);

  // 扣点 + 免费重生一次(不回滚):第1次扣点、第2次免费、第3次起再扣点。
  // units>1 用于影片按时长动态计价(每单位约 30 秒/120 字);其余动作 units=1。
  const confirmCharge = useCallback(async (action: 'content' | 'voice' | 'video' | 'video_agent', taskId: number, label: string, units = 1): Promise<boolean> => {
    const u = Math.max(1, units);
    let cnt = 0;
    // video_agent 较贵,不给免费重生;其余动作沿用「免费重生一次」
    if (action !== 'video_agent') { try { cnt = await getGenCount(taskId, action); } catch { /* 视为第一次 */ } }
    const isFreeRegen = cnt === 1;
    const base = costs[action] ?? 0;
    const cost = base * u;
    if (isFreeRegen) {
      if (!(await askConfirm('免费重新生成', `「${label}」这次是【免费重新生成】(每个任务有 1 次免费重生,结果不回滚、会覆盖显示)。确定要重新生成吗?`, { confirmLabel: '确定重新生成' }))) return false;
      return true;
    }
    const usedNote = cnt >= 2 ? '(免费重生额度已用过,本次照常扣点)' : '';
    const unitNote = u > 1 ? `\n(影片约 ${u * 30} 秒以内,按时长计:${u} 段 × ${base} 点)` : '';
    if (!(await askConfirm('确认生成', `「${label}」将扣除 ${cost} 点(不可退回)${unitNote}${usedNote}。确定要生成吗?`, { confirmLabel: '确定生成' }))) return false;
    try { await spendCreditsUnits(action, u, cid, taskId); } catch (e) { setError(e instanceof Error ? e.message : '点数不足,请联络管理员加值'); return false; }
    return true;
  }, [costs, cid, askConfirm]);

  const handleGenAvatar = useCallback(async () => {
    if (!(await askConfirm('生成真人形象', `生成真人形象会扣除 ${costs['avatar'] ?? 10} 点(不可退回)。确定生成吗?`, { confirmLabel: '确定生成' }))) return;
    try { await spendCredits('avatar', cid); } catch (e) { setError(e instanceof Error ? e.message : '点数不足'); return; }
    setAvatarBusy(true); setError(null); setInfo(null);
    try {
      const r = await generateAvatar(cid);
      if (r.ok && r.url) { setInfo('真人形象已生成!'); await load(); }
      else setError(r.message || '形象生成中,稍后重整即可看到');
    } catch (e) { setInfo(e instanceof Error ? `已送出(${e.message}),稍后重整看看` : '已送出,稍后重整'); await load(); }
    finally { setAvatarBusy(false); }
  }, [cid, costs, load]);

  const handleUploadAvatar = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadConsent) { setError('请先勾选「我已取得本人或肖像授权」'); if (avatarFileRef.current) avatarFileRef.current.value = ''; return; }
    setAvatarBusy(true); setError(null); setInfo(null);
    try {
      const dataUrl: string = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result || '')); r.onerror = rej; r.readAsDataURL(file); });
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const r = await uploadAvatar(cid, dataUrl, ext);
      if (r.ok && r.url) {
        await saveCharacterPhoto(cid, r.url, '用户上传授权照片', 'upload').catch(() => {});
        setInfo('头像已上传并加入角色相册!');
        await load();
      } else setError(r.message || '上传失败,请重试');
    } catch (err) { setError(err instanceof Error ? err.message : '上传失败'); }
    finally { setAvatarBusy(false); if (avatarFileRef.current) avatarFileRef.current.value = ''; }
  }, [cid, uploadConsent, load]);

  const handleSetAvatarFromLibrary = useCallback(async (url: string) => {
    setLibraryAvatarBusy(true); setError(null);
    try {
      await setAvatar(cid, url);
      await load();
      setSuccess('已更换为相册中的这张照片。');
    } catch (e) { setError(e instanceof Error ? e.message : '更换头像失败'); }
    finally { setLibraryAvatarBusy(false); }
  }, [cid, load]);

  const handleConfirmAsset = useCallback(async (assetId: number) => {
    setAssetConfirmBusy(true); setError(null);
    try {
      await confirmAsset(assetId);
      setSuccess('已同意存入你的档案库。');
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : '确认失败'); }
    finally { setAssetConfirmBusy(false); }
  }, [load]);

  const handleRejectAsset = useCallback(async (assetId: number) => {
    const ok = await askConfirm(PAID_GEN_ABANDON_TITLE, PAID_GEN_ABANDON_MESSAGE, { confirmLabel: '确定放弃', cancelLabel: '再看看' });
    if (!ok) return;
    setAssetConfirmBusy(true);
    try {
      await rejectAsset(assetId);
      setInfo('已放弃,未存入档案库。');
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : '操作失败'); }
    finally { setAssetConfirmBusy(false); }
  }, [load, askConfirm]);

  const handleRejectWardrobe = useCallback(async () => {
    const ok = await askConfirm(PAID_GEN_ABANDON_TITLE, PAID_GEN_ABANDON_WARDROBE_MESSAGE, { confirmLabel: '确定放弃', cancelLabel: '再看看' });
    if (!ok) return;
    setWardrobePreview(null);
  }, [askConfirm]);

  const handleGenerateContent = useCallback(async (taskId: number) => {
    if (!(await confirmCharge('content', taskId, '生成内容运营包'))) return;
    const jobId = `content-${taskId}`;
    trackJob({ id: jobId, label: '内容运营包生成中…', taskId, tab: 'content', startedAt: Date.now() });
    setGeneratingTaskId(taskId);
    setSelectedTaskId(taskId);
    setError(null);
    try { await generateContent(taskId); await load(); setActiveTab('content'); }
    catch (e) { setError(e instanceof Error ? e.message : '内容生成失败'); }
    finally { clearJob(jobId); setGeneratingTaskId(null); }
  }, [confirmCharge, load, trackJob, clearJob]);

  const handleVideoImage = useCallback(async (taskId: number) => {
    if (!character?.avatar_url) { setVideoNote({ taskId, tone: 'error', text: '这个角色还没有真人形象,无法用形象生成影片。' }); return; }
    if (!(await confirmCharge('video', taskId, '形象动态影片'))) return;
    const jobId = `video-image-${taskId}`;
    trackJob({ id: jobId, label: '形象动态影片生成中…', taskId, tab: 'asset', startedAt: Date.now() });
    setVideoNote({ taskId, tone: 'info', text: '正在送出形象动态影片请求…' });
    setVideoBusy({ taskId, mode: 'image_video' });
    setError(null); setInfo(null);
    try {
      const r = await generateVideoImage(cid, taskId);
      if (r && r.ok === false) setVideoNote({ taskId, tone: 'error', text: r.message || '影片生成尚未完成,请再试一次' });
      else setVideoNote({ taskId, tone: 'info', text: r.message || '形象动态影片已开始生成(约 2-3 分钟),完成后会自动出现在素材区。' });
    } catch (e) {
      setVideoNote({ taskId, tone: 'info', text: e instanceof Error ? `已送出,可能仍在处理中(${e.message}),稍后会自动出现在素材区。` : '已送出,稍后会自动出现在素材区。' });
    } finally { clearJob(jobId); await load(); setVideoBusy(null); }
  }, [confirmCharge, character, cid, load, trackJob, clearJob]);

  const handleVideoMinimax = useCallback(async (taskId: number) => {
    if (!character?.avatar_url) { setVideoNote({ taskId, tone: 'error', text: '这个角色还没有真人形象,无法生成真人感影片。' }); return; }
    if (!(await confirmCharge('video', taskId, '真人感影片'))) return;
    const jobId = `video-minimax-${taskId}`;
    trackJob({ id: jobId, label: '真人感影片生成中…', taskId, tab: 'asset', startedAt: Date.now() });
    setVideoNote({ taskId, tone: 'info', text: '正在送出真人感影片请求…' });
    setVideoBusy({ taskId, mode: 'minimax_video' });
    setError(null); setInfo(null);
    try {
      const r = await generateVideoMinimax(cid, taskId);
      if (r && r.ok === false) setVideoNote({ taskId, tone: 'error', text: r.message || '真人感影片生成尚未完成,请再试一次' });
      else setVideoNote({ taskId, tone: 'info', text: r.message || '真人感影片已开始生成(约 2-5 分钟),完成后会自动出现在素材区。' });
    } catch (e) {
      setVideoNote({ taskId, tone: 'info', text: e instanceof Error ? `已送出,可能仍在处理中(${e.message}),稍后会自动出现在素材区。` : '已送出,稍后会自动出现在素材区。' });
    } finally { clearJob(jobId); await load(); setVideoBusy(null); }
  }, [confirmCharge, cid, character, load, trackJob, clearJob]);

  const handleVideoSelfie = useCallback(async (taskId: number, script: string) => {
    if (!character?.avatar_url) { setVideoNote({ taskId, tone: 'error', text: '这个角色还没有真人形象/头贴,无法生成自拍口播影片。' }); return; }
    if (!(await confirmCharge('video', taskId, '自拍口播影片', unitsForChars(script.length)))) return;
    const jobId = `video-selfie-${taskId}`;
    trackJob({ id: jobId, label: '自拍口播影片生成中…', taskId, tab: 'asset', startedAt: Date.now() });
    setVideoNote({ taskId, tone: 'info', text: '正在生成自拍构图 + 角色配音 + 对嘴型影片…(约 2-4 分钟)' });
    setVideoBusy({ taskId, mode: 'selfie_talk' });
    setError(null); setInfo(null);
    try {
      const r = await generateVideoSelfie(cid, taskId, script);
      if (r && r.ok === false) setVideoNote({ taskId, tone: 'error', text: r.message || '自拍口播影片生成失败' });
      else setVideoNote({ taskId, tone: 'info', text: r.message || '自拍口播影片已开始生成,完成后会自动出现在素材区。' });
    } catch (e) {
      setVideoNote({ taskId, tone: 'info', text: e instanceof Error ? `已送出,可能仍在处理中(${e.message}),稍后会自动出现在素材区。` : '已送出,稍后会自动出现在素材区。' });
    } finally { clearJob(jobId); await load(); setVideoBusy(null); }
  }, [confirmCharge, character, cid, load, trackJob, clearJob]);

  const handleVoice = useCallback(async (taskId: number, text: string) => {
    if (!selectedVoice) { setError('请先选一个声音'); return; }
    if (!(await confirmCharge('voice', taskId, '中文配音'))) return;
    const jobId = `voice-${taskId}`;
    trackJob({ id: jobId, label: '中文配音生成中…', taskId, tab: 'asset', startedAt: Date.now() });
    setVoiceBusyTaskId(taskId);
    setError(null); setInfo(null);
    try {
      const r = await generateVoice(cid, taskId, selectedVoice, text);
      if (r && r.ok === false) setError(r.message || '配音生成失败');
      else setInfo('配音已生成,请看素材区');
    } catch (e) { setError(e instanceof Error ? e.message : '配音生成失败'); }
    finally { clearJob(jobId); await load(); setVoiceBusyTaskId(null); }
  }, [confirmCharge, cid, selectedVoice, load, trackJob, clearJob]);

  // 封面图:走生成中心文生图链(text_to_image),结果暂存 localStorage 并显示于素材区
  const handleCover = useCallback(async (taskId: number, prompt: string) => {
    const cost = costs['gen_t2i'] ?? 8;
    if (!(await askConfirm('生成封面图', `「生成封面图」将扣除 ${cost} 点(不可退回)。确定要生成吗?`, { confirmLabel: '确定生成' }))) return;
    const jobId = `cover-${taskId}`;
    trackJob({ id: jobId, label: '封面图生成中…', taskId, tab: 'asset', startedAt: Date.now() });
    setCoverBusyTaskId(taskId);
    setError(null); setInfo(null);
    try {
      const r = await genTextToImage(prompt, DEFAULT_PORTRAIT_ASPECT);
      if (r.url) {
        try { localStorage.setItem(coverKey(taskId), r.url); } catch { /* ignore */ }
        setCoverImages((prev) => ({ ...prev, [taskId]: { url: r.url } }));
        setInfo('封面图已生成,请看素材区。');
      } else setError(r.message || '封面图生成中,请稍后再试');
    } catch (e) { setError(e instanceof Error ? e.message : '封面图生成失败'); }
    finally { clearJob(jobId); setCoverBusyTaskId(null); }
  }, [costs, coverKey, askConfirm, trackJob, clearJob]);

  // 生成/更新角色落地与开发指令(约 60-100s,非每周,角色级)
  const handleGeneratePlaybook = useCallback(async () => {
    if (!(await askConfirm(
      '生成平台开发指令',
      '生成「角色落地与开发指令」会用 AI 依角色人设产出平台主页设定、主动开发对象、留言/私信破冰话术(约 1-2 分钟,会覆盖旧版)。\n\n点「取消」不会开始生成。',
      { confirmLabel: '确定生成' },
    ))) return;
    const jobId = 'playbook';
    trackJob({ id: jobId, label: '平台开发指令生成中…', startedAt: Date.now() });
    setPlaybookBusy(true); setError(null); setInfo(null);
    setShowPlaybook(true);
    try {
      await generateRolePlaybook(cid);
      const pb = await getRolePlaybook(cid);
      setPlaybook(pb);
      setInfo('角色开发指令已生成!');
    } catch {
      setInfo('开发指令仍在生成中(约 1-2 分钟),完成后回到这页或重整即可看到。');
      getRolePlaybook(cid).then(setPlaybook).catch(() => {});
    } finally { clearJob(jobId); setPlaybookBusy(false); }
  }, [cid, askConfirm, trackJob, clearJob]);

  // 保存验收回填到 DB(P0.3:取代 localStorage)
  const handleSaveMetrics = useCallback(async (task: BoardTask, values: Record<string, string>) => {
    if (!board) return;
    setMetricsSavingTaskId(task.task_id);
    setError(null);
    try {
      const rows = task.success_metrics
        .filter((m) => (values[m.metric_key] ?? '').toString().trim() !== '')
        .map((m) => ({ task_id: task.task_id, board_id: board.id, role_id: cid, platform: task.platform, metric_key: m.metric_key, metric_label: m.label, metric_value: String(values[m.metric_key]) }));
      if (rows.length === 0) { setInfo('请先填写至少一个指标数值再保存。'); return; }
      await saveTaskMetricResults(rows);
      setMetricResults((prev) => ({ ...prev, [task.task_id]: { ...(prev[task.task_id] || {}), ...values } }));
      setInfo(null);
      setSuccess('验收数据已保存(已写入资料库,下周策略会据此优化)。');
    } catch (e) { setError(e instanceof Error ? e.message : '保存失败'); }
    finally { setMetricsSavingTaskId(null); }
  }, [board, cid]);

  // 模特换衣(锁脸换装):独立 webhook，用角色形象图锁脸
  const handleWardrobe = useCallback(async () => {
    const prompt = wardrobePrompt.trim();
    if (!prompt) { setError('请先描述要换的服装/场景,例如:米色针织衫+牛仔裤,逛街生活感,自然光'); return; }
    if (!character?.avatar_url) { setError('这个角色还没有形象图,无法锁脸换装'); return; }
    const cost = costs['wardrobe_lockface'] ?? costs['gen_i2i'] ?? 8;
    const freeLeft = wardrobeFreeRemaining;
    const confirmMsg = freeLeft > 0
      ? `「模特换衣」本张形象图还可免费预览 ${freeLeft} 次（每张图共 2 次免费）。\n\n本次不扣点。生成后请你对比脸是否一样,满意再存入相册。`
      : `「模特换衣」将扣除 ${cost} 点(不可退回)。\n\n本张形象图的 2 次免费预览已用完。生成后需你确认脸一样才存入相册。`;
    if (!(await askConfirm('模特换衣', `${confirmMsg}\n\n点「取消」不会开始生成。`, { confirmLabel: '确定生成预览' }))) return;
    const jobId = 'wardrobe';
    trackJob({ id: jobId, label: '换装预览生成中…', startedAt: Date.now() });
    setWardrobeBusy(true); setError(null); setInfo(null); setSuccess(null); setWardrobePreview(null);
    try {
      const r = await genWardrobeLockface(wrapWardrobePrompt(prompt), character.avatar_url, DEFAULT_PORTRAIT_ASPECT);
      if (r.url) {
        try {
          const spend = await spendWardrobeLockface(cid);
          setWardrobeFreeRemaining(spend.free_remaining);
        } catch (e) { setError(e instanceof Error ? e.message : '点数不足'); return; }
        setWardrobePreview({ url: r.url, prompt });
        setSuccess('换装预览已生成,请对比左右两张脸,满意再点「同意存入角色相册」。');
      } else setError(r.message || '换装生成失败,请再试一次');
    } catch (e) { setError(e instanceof Error ? e.message : '换装生成失败'); }
    finally { clearJob(jobId); setWardrobeBusy(false); }
  }, [wardrobePrompt, character, costs, cid, wardrobeFreeRemaining, askConfirm, trackJob, clearJob]);

  const handleConfirmWardrobe = useCallback(async () => {
    if (!wardrobePreview || !character?.avatar_url) return;
    setWardrobeBusy(true); setError(null);
    try {
      await saveCharacterPhoto(cid, wardrobePreview.url, wardrobePreview.prompt, 'wardrobe', character.avatar_url);
      setWardrobePreview(null);
      setWardrobePrompt('');
      setSuccess('已存入角色相册(永久保存)。可从相册设为头像。');
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : '存入失败'); }
    finally { setWardrobeBusy(false); }
  }, [wardrobePreview, character, cid, load]);

  // 设定角色的 HeyGen 影片声音(可选;不选则 Video Agent 自动挑中文声)
  const handleSetHeygenVoice = useCallback(async (voiceId: string) => {
    try { await setHeygenVoice(cid, voiceId); await load(); setSuccess(voiceId ? '已设定角色影片声音(下次分镜短影音会用这个声音)。' : '已改回「自动挑中文声」。'); }
    catch (e) { setError(e instanceof Error ? e.message : '设定声音失败'); }
  }, [cid, load]);

  // AI 分镜短影音:① 生成分镜草稿(免费,qwen)给用户确认
  const handleStoryboard = useCallback(async (taskId: number, requirement: string, maxSeconds: number) => {
    setSbBusyTaskId(taskId); setError(null); setInfo(null); setSuccess(null);
    try {
      const sb = await generateStoryboard(cid, requirement, maxSeconds);
      if (sb && (sb.shots?.length || sb.video_prompt)) { setStoryboards((p) => ({ ...p, [taskId]: sb })); setSuccess('分镜草稿已生成,确认满意后再生成影片(才扣点)。'); }
      else setError('分镜生成中或失败,请再试一次');
    } catch (e) { setError(e instanceof Error ? e.message : '分镜生成失败'); }
    finally { setSbBusyTaskId(null); }
  }, [cid]);

  // ② 确认后渲染(HeyGen Video Agent,按时长动态扣点)
  const handleRenderAgent = useCallback(async (taskId: number) => {
    const sb = storyboards[taskId];
    if (!sb || !sb.video_prompt) { setError('请先生成分镜草稿'); return; }
    if (!character?.avatar_url) { setVideoNote({ taskId, tone: 'error', text: '这个角色还没有真人形象/头贴,无法生成分镜短影音。' }); return; }
    const maxSec = sb.max_seconds || 30;
    const units = Math.max(1, Math.ceil((sb.est_seconds || maxSec) / 30));
    if (!(await confirmCharge('video_agent', taskId, 'AI 分镜短影音', units))) return;
    const jobId = `agent-${taskId}`;
    trackJob({ id: jobId, label: '分镜短影音渲染中…', taskId, tab: 'asset', startedAt: Date.now() });
    setAgentBusyTaskId(taskId);
    setVideoNote({ taskId, tone: 'info', text: '正在送出 AI 分镜短影音渲染…(首次建立数字人脸会久一点)' });
    setError(null); setInfo(null); setSuccess(null);
    try {
      const r = await generateVideoAgent(cid, taskId, sb.video_prompt, maxSec);
      if (r && r.ok === false) setVideoNote({ taskId, tone: 'error', text: r.message || '渲染失败' });
      else setVideoNote({ taskId, tone: 'info', text: r.message || 'AI 分镜短影音已开始生成(约 2-6 分钟),完成后会自动出现在素材区。' });
    } catch (e) {
      setVideoNote({ taskId, tone: 'info', text: e instanceof Error ? `已送出,可能仍在处理中(${e.message}),稍后会自动出现。` : '已送出,稍后会自动出现在素材区。' });
    } finally { clearJob(jobId); await load(); setAgentBusyTaskId(null); }
  }, [storyboards, character, cid, confirmCharge, load, trackJob, clearJob]);

  useEffect(() => { load(); }, [load]);

  // 有进行中任务(含刷新前留下的)时自动轮询
  useEffect(() => {
    if (storedJobs.length === 0 && !videos.some((v) => v.status === 'generating') && !strategyPending) return;
    const t = setTimeout(() => { load(); }, 15000);
    return () => clearTimeout(t);
  }, [storedJobs, videos, strategyPending, load]);

  // 任一生成进行中:计时
  useEffect(() => {
    const busy = generating || strategyPending || generatingTaskId !== null || videoBusy !== null || voiceBusyTaskId !== null || coverBusyTaskId !== null || wardrobeBusy || sbBusyTaskId !== null || agentBusyTaskId !== null;
    if (!busy) { setElapsed(0); return; }
    setElapsed(0);
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating, strategyPending, generatingTaskId, videoBusy, voiceBusyTaskId, coverBusyTaskId, wardrobeBusy, sbBusyTaskId, agentBusyTaskId]);

  // 策略生成中:每 10 秒自动轮询
  useEffect(() => {
    if (!strategyPending) return;
    const t = setTimeout(() => { load(); }, 10000);
    return () => clearTimeout(t);
  }, [strategyPending, board, load]);

  const handleTogglePublished = useCallback(async (t: BoardTask) => {
    try { await setTaskFlag(t.task_id, { published: !t.published }); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : '标记失败'); }
  }, [load]);
  const markDownloaded = useCallback((taskId: number) => {
    setTaskFlag(taskId, { downloaded: true }).then(() => load()).catch(() => {});
  }, [load]);
  const downloadWithReminder = useCallback((taskId: number, doDownload: () => void) => {
    doDownload();
    markDownloaded(taskId);
    setError(null);
    setInfo('已开始下载。⚠️ 作品最多保留 30 天,请务必保存好!30 天内可再次下载,逾期会自动删除纪录档案。');
  }, [markDownloaded]);

  const handleGenerate = useCallback(async () => {
    const hasBoard = !!board;
    const msg = hasBoard
      ? `重新生成本周策略会进入「第 ${phase + 1} 阶段」,并覆盖目前这版策略与任务。每次生成都会扣除策略点数(不可退回)。确定要重新生成吗?`
      : '生成本周策略会扣除策略点数(不可退回)。确定要生成吗?';
    if (!(await askConfirm(hasBoard ? '重新生成本周策略' : '生成本周策略', msg, { confirmLabel: '确定生成' }))) return;
    try { await spendCredits('strategy', cid); } catch (e) { setError(e instanceof Error ? e.message : '点数不足'); return; }
    try { localStorage.setItem(STRAT_KEY, JSON.stringify({ at: Date.now(), prevId: board?.id || 0 })); } catch { /* ignore */ }
    setStrategyPending(true);
    setGenerating(true);
    setError(null);
    try { await generateStrategy(cid); await load(); }
    catch { setInfo('策略仍在生成中(约 1-2 分钟)。你可以离开,完成后回到这页会自动显示,不用重生。'); }
    finally { setGenerating(false); }
  }, [cid, load, board, phase, STRAT_KEY, askConfirm]);

  // 由现有资料派生任务驱动版型
  const taskBoard = useMemo(() => {
    if (!board) return null;
    // follow_up_question 备援:从角色 dm_playbook 取第一句追问(不硬写死);策略大脑任务级有给时优先用
    const followUpFallback = playbook?.data?.dm_playbook?.dm_openers?.map((o) => o.follow_up || '').find((s) => s) || '';
    return buildTaskBoard(board, tasks, outputs, { videos, audios, coverImages, followUpFallback }, character?.name || `角色 #${cid}`);
  }, [board, tasks, outputs, videos, audios, coverImages, character, cid, playbook]);

  // 计算任务的「实时」状态(覆盖派生的基础状态)
  const liveStatus = useCallback((t: BoardTask): TaskStatus => {
    if (generatingTaskId === t.task_id || voiceBusyTaskId === t.task_id || videoBusy?.taskId === t.task_id || coverBusyTaskId === t.task_id) return 'generating';
    if (videos.some((v) => v.task_id === t.task_id && v.status === 'generating')) return 'generating';
    return t.task_status;
  }, [generatingTaskId, voiceBusyTaskId, videoBusy, coverBusyTaskId, videos]);

  // 桌面版预设选第一个任务(仅在没有 session 恢复时)
  useEffect(() => {
    if (!uiRestored) return;
    if (taskBoard && taskBoard.tasks.length && selectedTaskId === null && typeof window !== 'undefined' && window.innerWidth >= 1024) {
      setSelectedTaskId(taskBoard.tasks[0].task_id);
    }
  }, [taskBoard, selectedTaskId, uiRestored]);

  const selectedTask = useMemo(() => taskBoard?.tasks.find((t) => t.task_id === selectedTaskId) || null, [taskBoard, selectedTaskId]);

  const openTask = useCallback((taskId: number, tab: DetailTab = 'exec') => {
    setSelectedTaskId(taskId);
    setActiveTab(tab);
    setTimeout(() => document.getElementById('task-detail-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, []);

  // 进行中任务一览(刷新/滑走回来仍看得到进度)
  const activeJobs = useMemo(() => {
    const jobs: { id: string; label: string; taskId?: number; tab?: DetailTab }[] = [];
    if (generating || strategyPending) jobs.push({ id: 'strategy', label: '本周策略生成中…' });
    if (generatingTaskId !== null) jobs.push({ id: 'content', label: '内容运营包生成中…', taskId: generatingTaskId, tab: 'content' });
    if (voiceBusyTaskId !== null) jobs.push({ id: 'voice', label: '中文配音生成中…', taskId: voiceBusyTaskId, tab: 'asset' });
    if (videoBusy) jobs.push({ id: `video-${videoBusy.mode}`, label: '影片生成中…', taskId: videoBusy.taskId, tab: 'asset' });
    if (coverBusyTaskId !== null) jobs.push({ id: 'cover', label: '封面图生成中…', taskId: coverBusyTaskId, tab: 'asset' });
    if (sbBusyTaskId !== null) jobs.push({ id: 'sb', label: '分镜草稿生成中…', taskId: sbBusyTaskId, tab: 'asset' });
    if (agentBusyTaskId !== null) jobs.push({ id: 'agent', label: '分镜短影音渲染中…', taskId: agentBusyTaskId, tab: 'asset' });
    if (wardrobeBusy) jobs.push({ id: 'wardrobe', label: '换装预览生成中…' });
    if (avatarBusy) jobs.push({ id: 'avatar', label: '形象处理中…' });
    for (const v of videos.filter((x) => x.status === 'generating' && x.task_id)) {
      if (!jobs.some((j) => j.taskId === v.task_id && j.label.includes('影片'))) {
        jobs.push({ id: `vid-${v.id}`, label: '后台影片生成中…', taskId: v.task_id ?? undefined, tab: 'asset' });
      }
    }
    for (const a of pendingAssets) {
      jobs.push({ id: `pending-${a.id}`, label: '待你确认入库', taskId: a.task_id ?? undefined, tab: 'asset' });
    }
    if (wardrobePreview) jobs.push({ id: 'wardrobe-preview', label: '换装预览待确认' });
    for (const sj of storedJobs) {
      if (!jobs.some((j) => j.id === sj.id)) jobs.push({ id: sj.id, label: sj.label, taskId: sj.taskId, tab: sj.tab });
    }
    return jobs;
  }, [generating, strategyPending, generatingTaskId, voiceBusyTaskId, videoBusy, coverBusyTaskId, sbBusyTaskId, agentBusyTaskId, wardrobeBusy, avatarBusy, videos, pendingAssets, wardrobePreview, storedJobs]);

  const anyBusy = generating || strategyPending || generatingTaskId !== null || videoBusy !== null || voiceBusyTaskId !== null || coverBusyTaskId !== null || wardrobeBusy || sbBusyTaskId !== null || agentBusyTaskId !== null;
  const hasPendingPaidResult = pendingAssets.length > 0 || !!wardrobePreview;
  const hasAsyncVideoGenerating = videos.some((v) => v.status === 'generating');

  usePaidGenerationGuard({
    isGenerating: anyBusy || avatarBusy || hasAsyncVideoGenerating,
    hasPendingResult: hasPendingPaidResult,
    label: wardrobePreview ? '换装预览' : pendingAssets.length ? '待确认素材' : '付费生成',
    askConfirm,
  });

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
      {adminManagingOther && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-accent-200 bg-accent-50/60 px-4 py-3 text-sm text-accent-900">
          <i className="ri-shield-user-line text-accent-600"></i>
          <span>管理员模式:你正在代为管理会员的角色,所有操作将计入该角色所属会员。</span>
          <Link to="/admin" className="ml-auto text-xs font-medium text-accent-700 hover:underline">返回营运管理中心</Link>
        </div>
      )}
      {character && !canManage && !loading && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          你没有权限管理此角色,仅可浏览。
        </div>
      )}
      {/* 顶部:角色 + 形象 + 生成策略 */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0 flex flex-col items-center gap-1.5">
            <div className="w-16 h-16 rounded-2xl overflow-hidden bg-primary-100 flex items-center justify-center ring-offset-2">
              {character?.avatar_url ? <img src={character.avatar_url} alt={character.name} className="w-full h-full object-cover" /> : <i className="ri-user-smile-line text-primary-600 text-2xl"></i>}
            </div>
            <div className="flex flex-col items-center gap-1 w-32">
              {character?.avatar_url ? (
                <button type="button" onClick={() => downloadFromUrl(`${character.name || 'avatar'}.png`, character.avatar_url as string)} className="inline-flex items-center gap-1 text-[11px] text-secondary-500 hover:text-foreground-700 cursor-pointer">
                  <i className="ri-download-line"></i>下载形象
                </button>
              ) : null}
              <button type="button" onClick={() => avatarFileRef.current?.click()} disabled={avatarBusy} className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium border border-primary-300 text-primary-700 hover:bg-primary-50 cursor-pointer disabled:opacity-50">
                <i className="ri-upload-2-line"></i>{character?.avatar_url ? '更换照片' : '上传照片'}
              </button>
              {!character?.avatar_url && (
                <button type="button" onClick={handleGenAvatar} disabled={avatarBusy} className={`w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium text-background-50 ${avatarBusy ? 'bg-primary-400 cursor-wait' : 'bg-primary-500 hover:bg-primary-600 cursor-pointer'}`}>
                  {avatarBusy ? '处理中…' : <><i className="ri-camera-line"></i>AI 生成</>}
                </button>
              )}
              <label className="flex items-start gap-1.5 text-[10px] sm:text-[11px] text-secondary-500 leading-snug cursor-pointer text-left w-full">
                <input type="checkbox" checked={uploadConsent} onChange={(e) => setUploadConsent(e.target.checked)} className="mt-0.5 flex-shrink-0" />
                <span>上传前请勾选:已取得本人/肖像授权</span>
              </label>
              <input ref={avatarFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUploadAvatar} />
              {characterPhotos.length > 0 && (
                <button type="button" onClick={() => setShowPhotoLibrary(true)} className="text-[10px] text-primary-600 hover:underline cursor-pointer">
                  相册 {characterPhotos.length} 张
                </button>
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
              <Link to="/board" className="hover:text-foreground-600">角色</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>任务板</span>
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">{character ? character.name : `角色 #${cid}`} · 本周任务板</h1>
            <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed max-w-2xl hidden sm:block">每个任务一目了然:点任务卡上的主按钮就开始生成,生成结果会直接显示在右侧任务详情里。</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          {canManage && (
            <button type="button" onClick={handleDeleteCharacter} disabled={deleting} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 cursor-pointer disabled:opacity-50">
              <i className="ri-delete-bin-line"></i>{deleting ? '删除中…' : '删除角色'}
            </button>
          )}
          <button onClick={handleGenerate} disabled={!canManage || generating || strategyPending} className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${generating || strategyPending || !canManage ? 'bg-primary-400 text-background-50 cursor-wait' : 'bg-primary-500 text-background-50 hover:bg-primary-600 active:scale-95'}`}>
            {generating || strategyPending ? (
              <><div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>AI 正在生成本周策略…(已 {elapsed}s)</>
            ) : (
              <><i className="ri-magic-line text-base"></i>{board ? '重新生成本周策略' : '生成本周策略'}</>
            )}
          </button>
        </div>
      </div>

      {/* 角色人设(收合) — 手机加大字号 */}
      {(profile || character?.description) && (
        <div className="mb-5 rounded-xl border border-secondary-200 bg-background-50">
          <button type="button" onClick={() => setShowProfile((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3.5 text-left cursor-pointer">
            <i className="ri-id-card-line text-primary-500 text-lg"></i>
            <span className="text-base sm:text-sm font-semibold text-foreground-900">角色人设与背景</span>
            <span className="text-xs text-secondary-400 hidden xs:inline">先了解这个角色是谁</span>
            <i className={`ri-arrow-down-s-line text-secondary-400 ml-auto transition-transform text-lg ${showProfile ? 'rotate-180' : ''}`}></i>
          </button>
          {showProfile && (
            <div className="px-4 pb-4 pt-1 border-t border-secondary-100">
              <ProfileSummary character={character} profile={profile} />
            </div>
          )}
        </div>
      )}

      {/* 待确认入库的素材 */}
      {pendingAssets.length > 0 && (
        <section className="mb-5 rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-amber-900"><i className="ri-inbox-archive-line mr-1"></i>待你确认入库 ({pendingAssets.length})</h3>
          <p className="text-[12px] sm:text-[11px] text-amber-800/90 leading-relaxed">AI 已生成预览,满意再点「同意存入档案库」才会进入你的正式素材库。</p>
          <div className="space-y-3">
            {pendingAssets.map((a) => (
              <PendingAssetCard key={a.id} asset={a} busy={assetConfirmBusy} onConfirm={handleConfirmAsset} onReject={handleRejectAsset} />
            ))}
          </div>
        </section>
      )}

      {/* 进行中 / 待确认 — 固定条,滑走回来仍看得到 */}
      {activeJobs.length > 0 && (
        <div className="mb-5 rounded-xl border border-primary-200 bg-primary-50/80 p-3 sm:p-4 sticky top-2 z-20 shadow-sm">
          <div className="text-sm font-semibold text-primary-900 mb-2 flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin flex-shrink-0"></div>
            进行中的工作 ({activeJobs.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {activeJobs.map((j) => (
              <button
                key={j.id}
                type="button"
                onClick={() => { if (j.taskId) openTask(j.taskId, j.tab || 'asset'); else if (j.id === 'wardrobe-preview') setShowWardrobe(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-background-50 border border-primary-200 text-primary-800 hover:bg-primary-100 cursor-pointer"
              >
                {j.label}
                {j.taskId ? <i className="ri-arrow-right-s-line"></i> : null}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-primary-700/80 mt-2">不小心滑走或刷新页面也没关系,点上方标签可回到对应任务。生成前都会弹出确认。</p>
        </div>
      )}

      {/* 进度横幅 */}
      {anyBusy && (() => {
        const label = (generating || strategyPending) ? 'AI 正在生成本周策略' : agentBusyTaskId !== null ? 'AI 分镜短影音生成中' : sbBusyTaskId !== null ? 'AI 分镜草稿生成中' : videoBusy !== null ? '影片生成中' : voiceBusyTaskId !== null ? '配音生成中' : coverBusyTaskId !== null ? '封面图生成中' : wardrobeBusy ? '换装图生成中' : '内容生成中';
        const slow = videoBusy !== null || strategyPending;
        return (
          <div className="mb-5 p-4 rounded-xl bg-primary-50 border border-primary-200">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin flex-shrink-0"></div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-primary-800">{label}<span className="ml-2 font-mono text-primary-600">已等候 {elapsed}s</span></div>
                <div className="text-[11px] text-primary-600/80 mt-0.5">正在处理中{slow ? '(影片约 2–5 分钟)' : ''}。<b>你可以先离开这页去做别的</b>,完成后回到这个角色就会自动看到结果。</div>
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full rounded-full bg-primary-100 overflow-hidden"><div className="h-full bg-primary-400 rounded-full animate-pulse" style={{ width: `${Math.min(95, 12 + elapsed * (slow ? 0.6 : 1.5))}%` }}></div></div>
          </div>
        );
      })()}

      {error && <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"><i className="ri-error-warning-line mr-1.5"></i>{error}</div>}
      {success && !error && <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700"><i className="ri-checkbox-circle-line mr-1.5"></i>{success}</div>}
      {info && !error && !success && <div className="mb-5 p-4 rounded-xl bg-primary-50 border border-primary-200 text-sm text-primary-700"><i className="ri-information-line mr-1.5"></i>{info}</div>}

      {loading ? (
        <div className="py-20 text-center text-sm text-secondary-400"><div className="inline-block w-6 h-6 border-2 border-secondary-200 border-t-primary-500 rounded-full animate-spin mb-3"></div><div>读取中…</div></div>
      ) : !board || !taskBoard ? (
        <div className="py-16 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center"><i className="ri-inbox-line text-secondary-400 text-xl"></i></div>
          <p className="text-sm text-foreground-600">{strategyPending ? '策略生成中(约 1-2 分钟),完成会自动出现,不用重生。你也可以先离开,回来就看得到。' : '这个角色还没有本周策略，点右上角「生成本周策略」开始。'}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* 逾期提醒 */}
          {!strategyPending && (() => {
            const days = Math.floor((Date.now() - new Date(board.created_at).getTime()) / 86400000);
            if (days < 6) return null;
            return (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                <i className="ri-alarm-warning-line text-amber-500 text-lg mt-0.5"></i>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-amber-800">本周策略已进行 {days} 天啰!该生成下一周策略了</div>
                  <div className="text-[12px] text-amber-700/90 mt-0.5">让角色持续有新作品可以曝光、养帐号。点右上角「重新生成本周策略」就会产生新一周的策略与任务。</div>
                </div>
                <button onClick={handleGenerate} disabled={generating || strategyPending} className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 cursor-pointer disabled:opacity-50"><i className="ri-magic-line"></i>生成下一周</button>
              </div>
            );
          })()}

          {/* 本周摘要(手机预设精简,可展开) */}
          {(() => {
            const ws = taskBoard.weekly_summary;
            return (
              <section className="bg-gradient-to-br from-primary-50/80 via-primary-50/40 to-background-50 rounded-xl border border-primary-200/60 p-4 md:p-6 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 text-[11px] font-semibold"><i className="ri-flag-2-line"></i>第 {phase} 阶段</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-[11px] font-medium"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>本周任务板</span>
                  <span className="text-[11px] text-secondary-400">进度 {taskBoard.board_metrics.tasks_completed}/{taskBoard.board_metrics.tasks_total}</span>
                </div>
                <div>
                  <div className="text-[11px] text-secondary-400 mb-0.5">本周目标</div>
                  <p className={`text-sm text-foreground-800 leading-relaxed ${showWeeklyDetail ? '' : 'line-clamp-2'}`}>{ws.goal}</p>
                </div>
                {!showWeeklyDetail && ws.focus.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {ws.focus.slice(0, 3).map((f, i) => (
                      <span key={i} className="inline-flex px-2 py-0.5 rounded-full bg-background-100 text-foreground-700 text-[10px] border border-secondary-200">{f}</span>
                    ))}
                    {ws.focus.length > 3 && <span className="text-[10px] text-secondary-400 self-center">+{ws.focus.length - 3}</span>}
                  </div>
                )}
                {showWeeklyDetail && (
                  <div className="space-y-2 pt-1 border-t border-primary-100">
                    {ws.focus.length > 0 && <div className="text-[12px] text-foreground-600"><span className="text-secondary-400">本周重点:</span>{ws.focus.join(' ｜ ')}</div>}
                    {ws.main_audience.length > 0 && <div className="text-[12px] text-foreground-600"><span className="text-secondary-400">主要受众:</span>{ws.main_audience.join(' ｜ ')}</div>}
                    {ws.private_domain_path && <div className="text-[12px] text-foreground-600"><span className="text-secondary-400">私域路径:</span>{ws.private_domain_path}</div>}
                    {ws.main_platforms.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[12px] text-secondary-400">主力平台:</span>
                        {ws.main_platforms.map((p) => <span key={p} className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px]">{pLabel(p)}</span>)}
                      </div>
                    )}
                  </div>
                )}
                <button type="button" onClick={() => setShowWeeklyDetail((v) => !v)} className="text-[11px] text-primary-600 hover:text-primary-700 cursor-pointer inline-flex items-center gap-0.5">
                  <i className={`ri-arrow-${showWeeklyDetail ? 'up' : 'down'}-s-line`}></i>
                  {showWeeklyDetail ? '收起本周详情' : '展开本周详情'}
                </button>
              </section>
            );
          })()}

          {/* AI经纪人今日简报 + 客群开发 SOP + 进入今日经营 */}
          {(todayExplanation || todayConversionSop) && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground-950">今天经营工作单</h3>
                <Link
                  to={`/materials/${cid}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line"></i>开始今日经营（勾选素材）
                </Link>
              </div>
              {todayExplanation && <TodayBriefingCard explanation={todayExplanation} />}
              {todayConversionSop && <ConversionPlaybookCard playbook={todayConversionSop} />}
            </section>
          )}

          {/* 今日优先任务 */}
          {taskBoard.today_priority && taskBoard.today_priority.task_id && (() => {
            const tp = taskBoard.today_priority!;
            return (
              <section className="rounded-xl border border-accent-200 bg-accent-50/50 p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-accent-700 mb-0.5"><i className="ri-fire-line mr-1"></i>今日先做</div>
                  <div className="text-sm sm:text-base font-semibold text-foreground-950 leading-snug line-clamp-2">{tp.title}</div>
                  <div className="mt-0.5 text-[11px] text-foreground-600 hidden sm:block">平台:{pLabel(tp.platform)}　·　{tp.reason}</div>
                </div>
                <button onClick={() => openTask(tp.task_id as number, 'exec')} className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-accent-500 text-white hover:bg-accent-600 cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-right-line"></i>{tp.action_label}
                </button>
              </section>
            );
          })()}

          {/* 本周节奏(手机横滑) */}
          {taskBoard.weekly_rhythm.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">本周节奏(第 1–7 天)</h3>
              <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-4">
                {taskBoard.weekly_rhythm.map((d) => {
                  const hasTask = d.task_ids.length > 0;
                  return (
                    <button key={d.day} onClick={() => { if (hasTask) openTask(d.task_ids[0], 'exec'); }}
                      className={`flex-shrink-0 w-[72vw] max-w-[200px] sm:w-auto sm:max-w-none snap-start text-left rounded-xl border p-3 transition-all ${hasTask ? 'bg-primary-50/60 border-primary-300 hover:border-primary-400 cursor-pointer' : 'bg-background-100 border-secondary-200 cursor-default'}`}>
                      <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-medium text-primary-600">{d.day}</span>{hasTask && <span className="text-[10px] text-primary-600"><span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 mr-1"></span>有任务 {d.task_ids.length}</span>}</div>
                      <div className="text-[11px] text-foreground-600 leading-relaxed line-clamp-2">{d.goal || '—'}</div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* 角色落地与开发指令(角色级,收合) */}
          <section className="rounded-xl border border-secondary-200 bg-background-50">
            <button type="button" onClick={() => setShowPlaybook((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left cursor-pointer">
              <i className="ri-compass-3-line text-primary-500"></i>
              <span className="text-sm sm:text-base font-semibold text-foreground-900">角色落地与开发指令</span>
              <span className="text-[11px] sm:text-xs text-secondary-400 hidden sm:inline">平台主页设定 · 主动开发 · 留言/私信破冰</span>
              {playbook?.updated_at && <span className="text-[10px] text-secondary-400">· 更新于 {new Date(playbook.updated_at).toLocaleDateString()}</span>}
              <i className={`ri-arrow-down-s-line text-secondary-400 ml-auto transition-transform ${showPlaybook ? 'rotate-180' : ''}`}></i>
            </button>
            {showPlaybook && (
              <div className="px-4 pb-4 pt-1 border-t border-secondary-100 space-y-4">
                {playbook?.data ? <RolePlaybookSection data={playbook.data} /> : (
                  <div className="py-4 text-center text-[15px] sm:text-sm text-secondary-500 leading-relaxed">还没有生成开发指令。展开后点下方按钮,AI 会依角色人设产出每个平台的名字/简介/置顶、要找哪类人、留言与私信破冰话术。</div>
                )}
                <div className="pt-2 border-t border-secondary-100">
                  <p className="text-[13px] sm:text-xs text-secondary-500 mb-2.5">生成前会弹出确认,点「取消」不会开始、也不会扣点。</p>
                  <button type="button" onClick={handleGeneratePlaybook} disabled={playbookBusy} className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${playbookBusy ? 'bg-primary-400 text-background-50 cursor-wait' : 'border-2 border-primary-400 text-primary-700 bg-primary-50 hover:bg-primary-100'}`}>
                    {playbookBusy ? <><div className="w-3.5 h-3.5 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>生成中…{elapsed}s</> : <><i className="ri-magic-line"></i>{playbook ? '更新平台指令(会确认)' : '生成平台指令(会确认)'}</>}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* 角色相册(永久) */}
          <section className="rounded-xl border border-secondary-200 bg-background-50">
            <button type="button" onClick={() => setShowPhotoLibrary((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left cursor-pointer">
              <i className="ri-gallery-line text-primary-500"></i>
              <span className="text-sm font-semibold text-foreground-900">角色相册</span>
              <span className="text-[11px] text-secondary-400">同一张脸·多角度换装·永久保存</span>
              <span className="text-[10px] text-primary-600 ml-auto">{characterPhotos.length} 张</span>
              <i className={`ri-arrow-down-s-line text-secondary-400 transition-transform ${showPhotoLibrary ? 'rotate-180' : ''}`}></i>
            </button>
            {showPhotoLibrary && (
              <div className="px-4 pb-4 pt-1 border-t border-secondary-100">
                <CharacterPhotoGrid
                  photos={characterPhotos}
                  currentAvatarUrl={character?.avatar_url}
                  setAvatarBusy={libraryAvatarBusy}
                  onSetAvatar={handleSetAvatarFromLibrary}
                  onDownload={(url, name) => downloadFromUrl(name, url)}
                />
              </div>
            )}
          </section>

          {/* 模特换衣(锁脸换装) */}
          {character?.avatar_url && (
            <section className="rounded-xl border border-secondary-200 bg-background-50">
              <button type="button" onClick={() => setShowWardrobe((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left cursor-pointer">
                <i className="ri-t-shirt-2-line text-primary-500"></i>
                <span className="text-sm font-semibold text-foreground-900">模特换衣(锁脸换装)</span>
                <span className="text-[11px] text-secondary-400 hidden sm:inline">确认满意才存入相册</span>
                <i className={`ri-arrow-down-s-line text-secondary-400 ml-auto transition-transform ${showWardrobe ? 'rotate-180' : ''}`}></i>
              </button>
              {showWardrobe && (
                <div className="px-4 pb-4 pt-1 border-t border-secondary-100 space-y-3">
                  <p className="text-[12px] sm:text-[11px] text-secondary-500 leading-relaxed">
                    保持同一张脸、同一身形,换服装/场景。生成后<b>不会自动入库</b>,请你对比确认脸与身材一致再存入角色相册。
                    {wardrobeFreeRemaining > 0
                      ? <>本张形象图还可<b>免费预览 {wardrobeFreeRemaining} 次</b>（每张图共 2 次）。</>
                      : <>本张形象图 2 次免费预览已用完,之后每次扣 {costs['wardrobe_lockface'] ?? costs['gen_i2i'] ?? 8} 点。</>}
                  </p>
                  <textarea
                    value={wardrobePrompt}
                    onChange={(e) => setWardrobePrompt(e.target.value)}
                    rows={2}
                    placeholder="描述要换的服装/场景,例如:米色针织衫配牛仔裤,周末逛街生活感,自然光街景"
                    className="w-full text-[14px] sm:text-[13px] border border-secondary-200 rounded-lg px-3 py-2.5 bg-background-100 focus:outline-none focus:border-primary-400 resize-none"
                  />
                  <button type="button" onClick={handleWardrobe} disabled={wardrobeBusy} className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${wardrobeBusy ? 'bg-primary-400 text-background-50 cursor-wait' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
                    {wardrobeBusy ? <><div className="w-3.5 h-3.5 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>生成预览中…</> : wardrobeFreeRemaining > 0
                      ? <><i className="ri-magic-line"></i>生成换装预览(免费·剩 {wardrobeFreeRemaining} 次)</>
                      : <><i className="ri-magic-line"></i>生成换装预览(扣 {costs['wardrobe_lockface'] ?? costs['gen_i2i'] ?? 8} 点)</>}
                  </button>
                  {wardrobePreview && (
                    <WardrobePreview
                      referenceUrl={character.avatar_url}
                      previewUrl={wardrobePreview.url}
                      prompt={wardrobePreview.prompt}
                      busy={wardrobeBusy}
                      onConfirm={handleConfirmWardrobe}
                      onReject={handleRejectWardrobe}
                    />
                  )}
                </div>
              )}
            </section>
          )}

          {/* 任务列表 + 任务详情(桌面双栏) */}
          <section className="lg:grid lg:grid-cols-12 lg:gap-5">
            {/* 左:任务列表 */}
            <div className={`${selectedTaskId !== null ? 'hidden lg:block' : 'block'} lg:col-span-5 space-y-2.5`}>
              <h3 className="text-base font-semibold text-foreground-950 mb-1">本周任务 <span className="text-xs text-secondary-400 font-normal">({taskBoard.tasks.length})</span></h3>
              {taskBoard.tasks.map((t) => {
                const st = liveStatus(t);
                const badge = TASK_STATUS_BADGE[st];
                const sel = selectedTaskId === t.task_id;
                return (
                  <div key={t.task_id} className={`rounded-xl border p-4 transition-all ${sel ? 'border-primary-400 ring-2 ring-primary-200 bg-primary-50/30' : 'border-secondary-200 bg-background-100'}`}>
                    <button type="button" onClick={() => openTask(t.task_id, 'exec')} className="w-full text-left cursor-pointer">
                      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                        {t.day_index ? <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 text-[10px] font-medium">第 {t.day_index} 天</span> : null}
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 text-[10px]">{pLabel(t.platform)}</span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 text-[10px]">{t.format}</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ml-auto ${badge.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`}></span>{badge.label}</span>
                      </div>
                      <div className="text-sm font-medium text-foreground-950 leading-snug line-clamp-2">{t.task_brief.title}</div>
                      {t.task_brief.goal && <div className="mt-1 text-[11px] text-secondary-500 hidden sm:block"><i className="ri-target-line mr-1"></i>{t.task_brief.goal}</div>}
                    </button>
                    <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => openTask(t.task_id, t.generated_content ? 'content' : 'exec')}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium border border-secondary-200 text-foreground-600 hover:bg-background-50 cursor-pointer"
                      >
                        查看详情<i className="ri-arrow-right-s-line"></i>
                      </button>
                      <button
                        type="button"
                        disabled={generatingTaskId === t.task_id}
                        onClick={() => handleGenerateContent(t.task_id)}
                        className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer disabled:opacity-50 ${t.generated_content ? 'border border-amber-300 text-amber-800 bg-amber-50' : 'bg-primary-500 text-background-50'}`}
                      >
                        <i className={t.generated_content ? 'ri-refresh-line' : 'ri-quill-pen-line'}></i>
                        {generatingTaskId === t.task_id ? '生成中…' : t.generated_content ? '重新生成(会确认)' : '生成内容(会确认)'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 右:任务详情 */}
            <div id="task-detail-panel" className={`${selectedTaskId !== null ? 'block' : 'hidden lg:block'} lg:col-span-7 mt-4 lg:mt-0`}>
              {!selectedTask ? (
                <div className="rounded-xl border border-dashed border-secondary-200 p-10 text-center text-sm text-secondary-400">选一个任务查看详情</div>
              ) : (
                <TaskDetail
                  task={selectedTask}
                  liveStatus={liveStatus(selectedTask)}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onBack={() => setSelectedTaskId(null)}
                  output={outputs[selectedTask.task_id]}
                  voices={roleVoices}
                  selectedVoice={selectedVoice}
                  setSelectedVoice={setSelectedVoice}
                  videos={videos.filter((v) => v.task_id === selectedTask.task_id)}
                  audios={audios.filter((a) => a.task_id === selectedTask.task_id)}
                  coverUrl={coverImages[selectedTask.task_id]?.url}
                  busy={{ content: generatingTaskId === selectedTask.task_id, voice: voiceBusyTaskId === selectedTask.task_id, video: videoBusy?.taskId === selectedTask.task_id ? videoBusy.mode : null, cover: coverBusyTaskId === selectedTask.task_id, anyVideo: videoBusy !== null }}
                  elapsed={elapsed}
                  videoNote={videoNote && videoNote.taskId === selectedTask.task_id ? videoNote : null}
                  characterName={character?.name || ''}
                  onGenerateContent={handleGenerateContent}
                  onVoice={handleVoice}
                  onVideoSelfie={handleVideoSelfie}
                  onVideoImage={handleVideoImage}
                  onVideoMinimax={handleVideoMinimax}
                  onCover={handleCover}
                  onTogglePublished={handleTogglePublished}
                  onDownload={downloadWithReminder}
                  metricValues={metricResults[selectedTask.task_id] || {}}
                  metricsSaving={metricsSavingTaskId === selectedTask.task_id}
                  onSaveMetrics={handleSaveMetrics}
                  storyboard={storyboards[selectedTask.task_id]}
                  sbBusy={sbBusyTaskId === selectedTask.task_id}
                  agentBusy={agentBusyTaskId === selectedTask.task_id}
                  onStoryboard={handleStoryboard}
                  onRenderAgent={handleRenderAgent}
                  videoAgentUnitCost={costs['video_agent'] ?? 30}
                  heygenVoices={heygenVoices}
                  heygenVoiceId={character?.heygen_voice_id ?? ''}
                  onSetHeygenVoice={handleSetHeygenVoice}
                />
              )}
            </div>
          </section>

          {/* 本周完整策略(顾问视角,预设收合) */}
          <section className="rounded-xl border border-secondary-200 bg-background-50">
            <button onClick={() => setShowFullStrategy((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left cursor-pointer">
              <i className="ri-book-2-line text-primary-500"></i>
              <span className="text-sm font-semibold text-foreground-900">本周完整商业策略(顾问视角)</span>
              <span className="text-[11px] text-secondary-400">想看全盘规划再展开</span>
              <i className={`ri-arrow-down-s-line text-secondary-400 ml-auto transition-transform ${showFullStrategy ? 'rotate-180' : ''}`}></i>
            </button>
            {showFullStrategy && <div className="px-4 pb-4 pt-1 border-t border-secondary-100"><FullStrategy board={board} /></div>}
          </section>
        </div>
      )}
      <ConfirmDialog
        open={!!confirmReq}
        title={confirmReq?.title ?? ''}
        message={confirmReq?.message ?? ''}
        confirmLabel={confirmReq?.confirmLabel}
        cancelLabel={confirmReq?.cancelLabel}
        onConfirm={() => { confirmReq?.resolve(true); setConfirmReq(null); }}
        onCancel={() => { confirmReq?.resolve(false); setConfirmReq(null); }}
      />
    </div>
  );
}

// ===== 任务详情面板 =====
interface TaskDetailProps {
  task: BoardTask;
  liveStatus: TaskStatus;
  activeTab: DetailTab;
  setActiveTab: (t: DetailTab) => void;
  onBack: () => void;
  output: DbTaskOutput | undefined;
  voices: DbMinimaxVoice[];
  selectedVoice: string;
  setSelectedVoice: (v: string) => void;
  videos: DbAsset[];
  audios: DbAsset[];
  coverUrl?: string;
  busy: { content: boolean; voice: boolean; video: 'image_video' | 'minimax_video' | 'selfie_talk' | null; cover: boolean; anyVideo: boolean };
  elapsed: number;
  videoNote: { taskId: number; tone: 'info' | 'error'; text: string } | null;
  characterName: string;
  onGenerateContent: (taskId: number) => void;
  onVoice: (taskId: number, text: string) => void;
  onVideoSelfie: (taskId: number, script: string) => void;
  onVideoImage: (taskId: number) => void;
  onVideoMinimax: (taskId: number) => void;
  onCover: (taskId: number, prompt: string) => void;
  onTogglePublished: (t: BoardTask) => void;
  onDownload: (taskId: number, doDownload: () => void) => void;
  metricValues: Record<string, string>;
  metricsSaving: boolean;
  onSaveMetrics: (task: BoardTask, values: Record<string, string>) => void;
  storyboard?: Storyboard;
  sbBusy: boolean;
  agentBusy: boolean;
  onStoryboard: (taskId: number, requirement: string, maxSeconds: number) => void;
  onRenderAgent: (taskId: number) => void;
  videoAgentUnitCost: number;
  heygenVoices: DbHeygenVoice[];
  heygenVoiceId: string;
  onSetHeygenVoice: (voiceId: string) => void;
}

function TaskDetail(props: TaskDetailProps) {
  const { task, liveStatus, activeTab, setActiveTab, onBack, output, voices, selectedVoice, setSelectedVoice, videos, audios, coverUrl, busy, elapsed, videoNote, characterName, onGenerateContent, onVoice, onVideoSelfie, onVideoImage, onVideoMinimax, onCover, onTogglePublished, onDownload, metricValues, metricsSaving, onSaveMetrics, storyboard, sbBusy, agentBusy, onStoryboard, onRenderAgent, videoAgentUnitCost, heygenVoices, heygenVoiceId, onSetHeygenVoice } = props;
  const [sbRequirement, setSbRequirement] = useState('');
  const [sbMaxSec, setSbMaxSec] = useState(15);
  const [hgTrialUrl, setHgTrialUrl] = useState('');
  const agentUnits = Math.max(1, Math.ceil(((storyboard?.est_seconds) || sbMaxSec) / 30));
  const badge = TASK_STATUS_BADGE[liveStatus];
  const gc = task.generated_content;
  const script = `${output?.title ?? ''}\n${output?.body ?? ''}`.trim();
  // 验收回填值由父层从 DB 提供;本地编辑态以 DB 值为初值,保存写回 DB(不依赖 localStorage)
  const [metricVals, setMetricVals] = useState<Record<string, string>>(metricValues);
  useEffect(() => { setMetricVals(metricValues); }, [metricValues, task.task_id]);
  const setMetric = (k: string, v: string) => setMetricVals((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="rounded-xl border border-secondary-200 bg-background-100 overflow-hidden">
      {/* 详情头 */}
      <div className="p-4 md:p-5 border-b border-secondary-100">
        <button onClick={onBack} className="lg:hidden inline-flex items-center gap-1 text-xs text-secondary-500 mb-2 cursor-pointer"><i className="ri-arrow-left-line"></i>返回任务列表</button>
        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
          {task.day_index ? <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 text-[10px] font-medium">第 {task.day_index} 天</span> : null}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 text-[10px]">{pLabel(task.platform)}</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 text-[10px]">{task.format}</span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${badge.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`}></span>{badge.label}</span>
        </div>
        <h3 className="text-base md:text-lg font-semibold text-foreground-950 leading-snug">{task.task_brief.title}</h3>
        {task.task_brief.display_summary && <p className="mt-1 text-[12px] text-secondary-500 leading-relaxed">{task.task_brief.display_summary}</p>}
      </div>

      {/* tabs */}
      <div className="flex items-stretch border-b border-secondary-100 overflow-x-auto">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setActiveTab(tb.key)} className={`flex items-center gap-1 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${activeTab === tb.key ? 'border-primary-500 text-primary-700' : 'border-transparent text-secondary-500 hover:text-foreground-700'}`}>
            <i className={tb.icon}></i>{tb.label}
          </button>
        ))}
      </div>

      <div className="p-4 md:p-5">
        {/* 执行指令 */}
        {activeTab === 'exec' && (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-foreground-800">今天照做:</div>
            <ol className="space-y-1.5">
              {task.execution_instructions.map((s, i) => (
                <li key={i} className="flex gap-2 text-[13px] text-foreground-700 leading-relaxed"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[11px] flex items-center justify-center font-medium">{i + 1}</span><span>{s}</span></li>
              ))}
              {task.execution_instructions.length === 0 && <li className="text-[12px] text-secondary-400">点下方「{task.task_brief.next_action}」生成内容,系统会给出执行要点。</li>}
            </ol>
            <button onClick={() => onGenerateContent(task.task_id)} disabled={busy.content} className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${busy.content ? 'bg-primary-400 text-background-50 cursor-wait' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
              {busy.content ? <><div className="w-3.5 h-3.5 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>生成中…{elapsed}s</> : <><i className={gc ? 'ri-refresh-line' : 'ri-quill-pen-line'}></i>{gc ? '重新生成(会确认)' : `${task.task_brief.next_action}(会确认)`}</>}
            </button>
          </div>
        )}

        {/* 生成内容 */}
        {activeTab === 'content' && (
          <div className="space-y-3">
            {!gc ? (
              <div className="text-center py-8">
                <div className="text-sm text-secondary-500 mb-3">还没有生成内容。点下方按钮,AI 会以角色第一人称写好标题、正文与承接话术。</div>
                <button onClick={() => onGenerateContent(task.task_id)} disabled={busy.content} className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${busy.content ? 'bg-primary-400 text-background-50 cursor-wait' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
                  {busy.content ? <><div className="w-3.5 h-3.5 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>生成中…{elapsed}s</> : <><i className="ri-quill-pen-line"></i>{task.task_brief.next_action}(会确认)</>}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-foreground-900">{gc.title}</span>
                  {output && (() => { const r = RISK[output.risk_level ?? 'low'] ?? RISK.low; return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${r.color}`}><span className={`w-1.5 h-1.5 rounded-full ${r.dot}`}></span>封号风险:{r.label}</span>; })()}
                  <button onClick={() => onDownload(task.task_id, () => downloadText(`${gc.title || 'content'}.txt`, `${gc.title}\n\n${gc.script || gc.body || ''}\n\n${gc.cta || ''}`))} className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-secondary-200 text-foreground-600 hover:border-secondary-300 cursor-pointer"><i className="ri-download-line"></i>下载文案</button>
                </div>
                {output?.risk_issues && output.risk_issues.length > 0 && <div className="text-[11px] text-red-600"><i className="ri-error-warning-line mr-1"></i>风险点:{output.risk_issues.join('、')}</div>}
                {gc.hook && <div className="text-[12px] leading-relaxed"><span className="text-primary-600 font-medium">开头钩子｜</span>{gc.hook}</div>}
                <div className="rounded-lg bg-background-50 border border-secondary-200 p-3 text-[13px] text-foreground-700 leading-relaxed whitespace-pre-wrap">{gc.script || gc.body}</div>
                {gc.cta && <div className="text-[12px] leading-relaxed"><span className="text-primary-600 font-medium">结尾 CTA｜</span>{gc.cta}</div>}
                <button onClick={() => onGenerateContent(task.task_id)} disabled={busy.content} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-secondary-200 text-foreground-600 hover:border-secondary-300 cursor-pointer">
                  {busy.content ? '生成中…' : <><i className="ri-refresh-line"></i>重新生成(会确认)</>}
                </button>
              </>
            )}
          </div>
        )}

        {/* 私域承接 */}
        {activeTab === 'pd' && (() => {
          const pd = task.private_domain;
          return (
            <div className="space-y-2.5 text-[13px] text-foreground-700">
              {pd.comment_keyword && <div><span className="text-secondary-400">评论关键词:</span><span className="ml-1 inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-700 font-medium">【{pd.comment_keyword}】</span></div>}
              {pd.public_reply && <div><div className="text-secondary-400 mb-0.5">公开回复:</div><div className="rounded-lg bg-background-50 border border-secondary-200 p-2.5 leading-relaxed">{pd.public_reply}</div></div>}
              {pd.private_reply && <div><div className="text-secondary-400 mb-0.5">私信第一句:</div><div className="rounded-lg bg-background-50 border border-secondary-200 p-2.5 leading-relaxed">{pd.private_reply}</div></div>}
              {pd.follow_up_question && <div><span className="text-secondary-400">追问:</span>{pd.follow_up_question}</div>}
              {pd.lead_magnet?.asset_title && <div className="rounded-md bg-accent-50/60 border border-accent-100 p-2.5 text-[12px] text-accent-700"><i className="ri-gift-line mr-1"></i>承接物:{pd.lead_magnet.asset_type}｜{pd.lead_magnet.asset_title}</div>}
              {pd.next_step && <div><span className="text-secondary-400">下一步:</span>{pd.next_step}</div>}
              {!pd.comment_keyword && !pd.public_reply && !pd.private_reply && <div className="text-[12px] text-secondary-400">生成内容后会带出更完整的私域承接话术。</div>}
            </div>
          );
        })()}

        {/* 素材 */}
        {activeTab === 'asset' && (
          <div className="space-y-3">
            {task.task_type === 'short_video' && (
              <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-3 space-y-2.5">
                <div className="text-[13px] font-semibold text-foreground-900"><i className="ri-movie-2-line text-primary-500 mr-1"></i>AI 分镜短影音(锁脸 + 角色声音,自动合成)</div>
                <p className="text-[10px] text-secondary-400 leading-relaxed">先<b>免费</b>生成「分镜 + 口播」给你确认(可改需求重生,不花钱);满意才<b>扣点渲染</b>成一支竖屏短影音(HeyGen Video Agent 自动合成多镜头,锁脸用角色形象)。</p>
                <textarea value={sbRequirement} onChange={(e) => setSbRequirement(e.target.value)} rows={2} placeholder="特殊需求(可留空):例如强调省钱、轻松生活感、想带某个主题…" className="w-full text-[12px] border border-secondary-200 rounded-md px-2.5 py-1.5 bg-background-100 focus:outline-none focus:border-primary-400 resize-none" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-secondary-500">时长</span>
                  {[5, 15, 30, 60].map((s) => (
                    <button key={s} onClick={() => setSbMaxSec(s)} className={`px-2 py-0.5 rounded text-[11px] border cursor-pointer ${sbMaxSec === s ? 'bg-primary-50 text-primary-700 border-primary-300' : 'bg-background-50 text-secondary-500 border-secondary-200'}`}>{s}秒内</button>
                  ))}
                </div>
                {heygenVoices.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-secondary-500">影片声音</span>
                    <HeygenVoiceSelect
                      voices={heygenVoices}
                      value={heygenVoiceId}
                      onChange={(id) => { onSetHeygenVoice(id); const v = heygenVoices.find((x) => x.voice_id === id); setHgTrialUrl(v?.preview_audio_url || ''); }}
                      className="text-[11px] border border-secondary-200 rounded-md px-2 py-1 bg-background-100 focus:outline-none focus:border-primary-400 max-w-[200px]"
                      allowAuto
                    />
                    {(() => { const cur = heygenVoices.find((x) => x.voice_id === heygenVoiceId); const url = hgTrialUrl || cur?.preview_audio_url || ''; return url ? <button onClick={() => setHgTrialUrl(url)} className="text-[10px] text-primary-600 hover:underline cursor-pointer"><i className="ri-play-circle-line"></i>试听</button> : null; })()}
                  </div>
                )}
                {hgTrialUrl && <audio src={hgTrialUrl} controls autoPlay className="h-7 w-full" />}
                <button onClick={() => onStoryboard(task.task_id, sbRequirement, sbMaxSec)} disabled={sbBusy || agentBusy} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium cursor-pointer border ${sbBusy ? 'border-secondary-200 text-secondary-400 cursor-wait' : 'border-primary-300 text-primary-700 hover:bg-primary-50'}`}>
                  {sbBusy ? <><div className="w-3 h-3 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>生成分镜中…</> : <><i className={storyboard ? 'ri-refresh-line' : 'ri-magic-line'}></i>{storyboard ? '重新生成分镜(免费)' : '生成分镜(免费)'}</>}
                </button>
                {storyboard && (storyboard.shots?.length || storyboard.video_prompt) && (
                  <div className="rounded-md bg-background-50 border border-secondary-200 p-2.5 space-y-2">
                    {storyboard.title && <div className="text-[12px] font-semibold text-foreground-900">{storyboard.title}</div>}
                    <ol className="space-y-1.5">
                      {(storyboard.shots || []).map((s, i) => (
                        <li key={i} className="text-[11px] text-foreground-700 leading-relaxed">
                          <span className="inline-flex w-4 h-4 rounded-full bg-primary-100 text-primary-700 text-[9px] items-center justify-center mr-1">{i + 1}</span>
                          <span className="text-secondary-500">画面:</span>{s.scene}　<span className="text-secondary-500">口播:</span><b>{s.narration}</b>
                        </li>
                      ))}
                    </ol>
                    <div className="text-[11px] text-foreground-600">预计 {storyboard.est_seconds || sbMaxSec} 秒 · 渲染约扣 <b className="text-primary-700">{agentUnits * videoAgentUnitCost} 点</b></div>
                    <button onClick={() => onRenderAgent(task.task_id)} disabled={agentBusy || sbBusy} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-semibold cursor-pointer ${agentBusy ? 'bg-primary-400 text-background-50 cursor-wait' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
                      {agentBusy ? <><div className="w-3.5 h-3.5 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>渲染中…{elapsed}s</> : <><i className="ri-vidicon-line"></i>确认并生成影片(扣 {agentUnits * videoAgentUnitCost} 点)</>}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] text-foreground-700">中文配音:</span>
              <MinimaxVoiceSelect
                voices={voices}
                value={selectedVoice}
                onChange={setSelectedVoice}
                className="text-[12px] border border-secondary-200 rounded-md px-2 py-1 bg-background-50 focus:outline-none focus:border-accent-400 max-w-[220px]"
              />
            </div>
            {task.asset_requests.map((ar) => {
              const isVoice = ar.asset_type === 'voice';
              const isTalk = ar.asset_type === 'talking_avatar_video';
              const isDyn = ar.asset_type === 'dynamic_video';
              const isCover = ar.asset_type === 'cover_image';
              const reqBusy = (isVoice && busy.voice) || (isTalk && busy.video === 'selfie_talk') || (isDyn && busy.video === 'image_video') || (isCover && busy.cover);
              const disabled = busy.anyVideo || busy.voice || busy.cover || busy.content;
              const needContent = (isVoice || isTalk) && !output;
              return (
                <div key={ar.asset_request_id} className="rounded-lg border border-secondary-200 bg-background-50 p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-foreground-800">{ar.label}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] ${ar.status === 'completed' ? 'bg-green-50 text-green-700' : ar.status === 'processing' || reqBusy ? 'bg-amber-50 text-amber-700' : 'bg-secondary-100 text-secondary-500'}`}>{ar.status === 'completed' ? '已生成' : ar.status === 'processing' || reqBusy ? '生成中' : '待生成'}</span>
                    <button
                      onClick={() => {
                        if (isVoice) onVoice(task.task_id, script);
                        else if (isTalk) onVideoSelfie(task.task_id, script);
                        else if (isDyn) onVideoImage(task.task_id);
                        else if (isCover) onCover(task.task_id, `${pLabel(task.platform)}风格封面图,干净、生活感、醒目标题:${task.task_brief.title}`);
                      }}
                      disabled={disabled || needContent}
                      title={needContent ? '请先在「生成内容」生成脚本' : ''}
                      className={`ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium cursor-pointer border ${disabled || needContent ? 'border-secondary-200 text-secondary-400 cursor-not-allowed' : 'border-primary-300 text-primary-700 hover:bg-primary-50'}`}
                    >
                      {reqBusy ? <><div className="w-3 h-3 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>生成中…</> : <><i className={ar.status === 'completed' ? 'ri-refresh-line' : 'ri-add-line'}></i>{ar.status === 'completed' ? '重新生成(会确认)' : `${ar.label}(会确认)`}</>}
                    </button>
                  </div>
                  {needContent && <div className="mt-1 text-[10px] text-secondary-400">需要先在「生成内容」产出脚本,才能配音/做口播影片。</div>}
                  {isDyn && <div className="mt-1 text-[10px] text-secondary-400">形象动态/真人感影片:只让形象动、无口型;要会讲话请用「口播影片」。</div>}
                  {isTalk && <div className="mt-1 text-[10px] text-secondary-400">MVP 自拍口播:角色形象 → 自拍构图 → Voice Engine 配音 → HeyGen 对嘴 → 单一 mp4。</div>}
                </div>
              );
            })}

            {videoNote && <div className={`p-2.5 rounded-lg text-[11px] leading-relaxed border ${videoNote.tone === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-primary-50 border-primary-200 text-primary-700'}`}><i className={`mr-1 ${videoNote.tone === 'error' ? 'ri-error-warning-line' : 'ri-information-line'}`}></i>{videoNote.text}</div>}

            {/* 已生成素材预览 */}
            <div className="space-y-2.5">
              {coverUrl && (
                <div className="rounded-lg bg-background-50 border border-secondary-200 p-2.5">
                  <div className="text-[10px] text-secondary-500 mb-1.5"><i className="ri-image-line"></i> 封面图</div>
                  <img src={coverUrl} alt="封面图" className="w-40 rounded-md" />
                  <button onClick={() => onDownload(task.task_id, () => downloadFromUrl(`${characterName || 'cover'}_${task.task_id}.png`, coverUrl))} className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-secondary-500 hover:text-foreground-700 cursor-pointer"><i className="ri-download-line"></i>下载</button>
                </div>
              )}
              {audios.map((a) => (
                <div key={a.id} className="flex items-center gap-2 rounded-md bg-background-50 border border-secondary-200 px-2.5 py-1.5">
                  <span className="text-[10px] text-secondary-500 whitespace-nowrap"><i className="ri-mic-2-line"></i> 配音</span>
                  {a.url && <audio src={a.url} controls className="h-7 flex-1 min-w-0" />}
                  {a.url && <button onClick={() => onDownload(task.task_id, () => downloadFromUrl(`${characterName || 'voice'}_${a.id}.mp3`, a.url as string))} className="text-[10px] text-secondary-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"><i className="ri-download-line"></i>下载</button>}
                </div>
              ))}
              {videos.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {videos.map((v) => (
                    <div key={v.id} className="rounded-lg bg-background-50 border border-secondary-200 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 text-[9px] font-medium">{(() => { const m = v.meta && (v.meta as Record<string, unknown>).mode; return m === 'video_agent' ? 'AI 分镜短影音' : m === 'heygen_photo' ? '口播影片' : m === 'digital_human' ? '数字人' : m === 'minimax_video' ? '真人感影片' : m === 'selfie_talk' ? '自拍口播' : '形象动态'; })()}</span>
                        {v.status === 'ready' && v.url ? <span className="text-[9px] text-green-600"><i className="ri-checkbox-circle-line"></i> 已完成</span> : <span className="text-[9px] text-amber-600"><i className="ri-loader-4-line"></i> 生成中…</span>}
                      </div>
                      {v.status === 'ready' && v.url ? (
                        <>
                          <video src={v.url} controls className="w-full rounded-md bg-black aspect-[9/16] object-contain max-h-64" />
                          <button onClick={() => onDownload(task.task_id, () => downloadFromUrl(`${characterName || 'video'}_${v.id}.mp4`, v.url as string))} className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-secondary-500 hover:text-foreground-700 cursor-pointer"><i className="ri-download-line"></i>下载影片</button>
                        </>
                      ) : (
                        <div className="w-full aspect-[9/16] max-h-64 rounded-md bg-secondary-100 flex items-center justify-center text-[10px] text-secondary-400"><div className="text-center"><div className="w-5 h-5 mx-auto mb-1 border-2 border-secondary-300 border-t-primary-500 rounded-full animate-spin"></div>影片生成中</div></div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 验收 */}
        {activeTab === 'metrics' && (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-foreground-800">今天看这几个(填写实际数据,保存后写入资料库,下周策略会读取做优化):</div>
            {task.success_metrics.length === 0 && <div className="text-[12px] text-secondary-400">尚无验收指标。</div>}
            <div className="space-y-2">
              {task.success_metrics.map((m) => (
                <div key={m.metric_key} className="flex items-center gap-2">
                  <span className="flex-1 text-[13px] text-foreground-700">{m.label}{m.target && <span className="text-secondary-400 text-[11px]">(目标:{m.target})</span>}</span>
                  <input type="number" value={metricVals[m.metric_key] ?? ''} onChange={(e) => setMetric(m.metric_key, e.target.value)} placeholder="填写" className="w-24 text-[12px] border border-secondary-200 rounded-md px-2 py-1 bg-background-50 focus:outline-none focus:border-primary-400" />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {task.success_metrics.length > 0 && (
                <button onClick={() => onSaveMetrics(task, metricVals)} disabled={metricsSaving} className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${metricsSaving ? 'bg-secondary-200 text-secondary-500 cursor-wait' : 'border border-primary-300 text-primary-700 hover:bg-primary-50'}`}>
                  {metricsSaving ? <><div className="w-3.5 h-3.5 border-2 border-secondary-300 border-t-primary-500 rounded-full animate-spin"></div>保存中…</> : <><i className="ri-save-line"></i>保存结果</>}
                </button>
              )}
              <button onClick={() => onTogglePublished(task)} className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${task.published ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
                <i className={task.published ? 'ri-checkbox-circle-fill' : 'ri-checkbox-circle-line'}></i>{task.published ? '已完成(点此取消)' : '标记完成'}
              </button>
            </div>
          </div>
        )}

        {/* 详细策略(预设最后,且非预设展开) */}
        {activeTab === 'detail' && (() => {
          const sd = task.strategy_detail;
          const Row = ({ label, value }: { label: string; value?: string }) => value ? <div className="text-[13px] leading-relaxed"><span className="text-secondary-400">{label}:</span><span className="text-foreground-700 ml-1">{value}</span></div> : null;
          return (
            <div className="space-y-2">
              <Row label="安排原因" value={sd.reason} />
              <Row label="受众分析" value={sd.audience_analysis} />
              <Row label="平台理由" value={sd.platform_reason} />
              {sd.risk_notes && <div className="text-[13px] leading-relaxed text-red-600"><span className="text-secondary-400">风险提醒:</span><span className="ml-1">{sd.risk_notes}</span></div>}
              <Row label="备用调整" value={sd.fallback_plan} />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ===== 本周完整商业策略(顾问视角,收合内容)=====
function FullStrategy({ board }: { board: DbWeeklyBoard }) {
  const bs = board.business_strategy || {};
  const chip = (t: string, i: number, cls = 'bg-primary-50 text-primary-700') => <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${cls}`}>{t}</span>;
  const lwr = bs.last_week_review;
  const hasReview = lwr && ((lwr.effective_content_types?.length || 0) + (lwr.failed_tasks?.length || 0) + (lwr.platforms_to_strengthen?.length || 0) + (lwr.directions_to_reduce?.length || 0) > 0);
  return (
    <div className="space-y-4 pt-2">
      {hasReview && lwr && (
        <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 p-3">
          <div className="text-sm font-semibold text-indigo-800 mb-1.5"><i className="ri-history-line mr-1"></i>上周复盘(本周据此优化)</div>
          {lwr.effective_content_types && lwr.effective_content_types.length > 0 && <div className="text-[12px] text-foreground-700 mb-0.5"><span className="text-secondary-400">上周有效:</span>{lwr.effective_content_types.join('、')}</div>}
          {lwr.failed_tasks && lwr.failed_tasks.length > 0 && <div className="text-[12px] text-foreground-700 mb-0.5"><span className="text-secondary-400">上周失效:</span>{lwr.failed_tasks.join('、')}</div>}
          {lwr.platforms_to_strengthen && lwr.platforms_to_strengthen.length > 0 && <div className="text-[12px] text-green-700 mb-0.5"><span className="text-secondary-400">本周加强:</span>{lwr.platforms_to_strengthen.map((p) => pLabel(p)).join('、')}</div>}
          {lwr.directions_to_reduce && lwr.directions_to_reduce.length > 0 && <div className="text-[12px] text-red-600"><span className="text-secondary-400">本周降低:</span>{lwr.directions_to_reduce.join('、')}</div>}
        </div>
      )}
      <div>
        <h4 className="text-sm font-semibold text-foreground-950 mb-1"><i className="ri-target-line text-primary-500 mr-1"></i>本周商业目标</h4>
        <p className="text-[13px] text-foreground-700 leading-relaxed">{bs.what_you_get || board.weekly_goal}</p>
        {bs.expected_effect && bs.expected_effect.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{bs.expected_effect.map((t, i) => chip(t, i, 'bg-green-50 text-green-700'))}</div>}
      </div>
      {bs.do_not_this_week && bs.do_not_this_week.length > 0 && (
        <div className="rounded-lg bg-red-50/70 border border-red-100 p-3"><div className="text-xs font-semibold text-red-700 mb-1"><i className="ri-forbid-line mr-1"></i>这周不要做</div><ul className="text-[12px] text-red-600/90 leading-relaxed list-disc ml-4">{bs.do_not_this_week.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
      )}
      {bs.audience_targets && bs.audience_targets.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground-950 mb-2"><i className="ri-group-line text-primary-500 mr-1"></i>本周要吸引的受众</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">{bs.audience_targets.map((a, i) => (
            <div key={i} className="rounded-lg bg-background-100 border border-secondary-200 p-3">{a.layer && <span className="inline-block mb-1 px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 text-[10px]">{a.layer}</span>}<div className="text-xs font-medium text-foreground-900">{a.audience_type}</div>{a.pain_points && a.pain_points.length > 0 && <div className="text-[11px] text-secondary-500 mt-0.5">痛点:{a.pain_points.join('、')}</div>}{a.content_angle && <div className="text-[11px] text-foreground-600 mt-0.5">切角:{a.content_angle}</div>}</div>
          ))}</div>
        </div>
      )}
      {bs.platform_roles && bs.platform_roles.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground-950 mb-2"><i className="ri-apps-2-line text-primary-500 mr-1"></i>各平台分工</h4>
          <div className="space-y-1.5">{bs.platform_roles.map((p, i) => (<div key={i} className="flex items-start gap-2 text-xs"><span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium flex-shrink-0">{pLabel(p.platform || '')}</span><span className="text-foreground-700">{p.platform_task}{p.lead_path ? <span className="text-secondary-400"> · 承接:{p.lead_path}</span> : null}</span></div>))}</div>
        </div>
      )}
      {bs.private_domain_plan && (
        <div className="rounded-lg bg-purple-50/60 border border-purple-100 p-3">
          <div className="text-sm font-semibold text-purple-800 mb-1.5"><i className="ri-route-line mr-1"></i>本周私域引流路径</div>
          {bs.private_domain_plan.path && bs.private_domain_plan.path.length > 0 && <div className="text-[12px] text-purple-700 mb-1.5">{bs.private_domain_plan.path.join(' → ')}</div>}
          {bs.private_domain_plan.comment_keywords && bs.private_domain_plan.comment_keywords.length > 0 && <div className="text-[11px] text-purple-700/90 mb-1">评论关键词:{bs.private_domain_plan.comment_keywords.map((k) => `【${k}】`).join(' ')}</div>}
          {bs.private_domain_plan.no_hard_drainage && <div className="text-[11px] text-red-500 mt-1"><i className="ri-error-warning-line mr-0.5"></i>{bs.private_domain_plan.no_hard_drainage}</div>}
        </div>
      )}
      {bs.active_outreach_plan && (bs.active_outreach_plan.daily_actions?.length || bs.active_outreach_plan.where_to_find_users?.length) && (
        <div className="rounded-lg bg-blue-50/60 border border-blue-100 p-3">
          <div className="text-sm font-semibold text-blue-800 mb-1.5"><i className="ri-radar-line mr-1"></i>本周主动开发任务</div>
          {bs.active_outreach_plan.daily_actions && bs.active_outreach_plan.daily_actions.length > 0 && <ul className="text-[12px] text-blue-700 leading-relaxed list-disc ml-4">{bs.active_outreach_plan.daily_actions.map((t, i) => <li key={i}>{t}</li>)}</ul>}
        </div>
      )}
      {bs.success_metrics && (
        <div>
          <h4 className="text-sm font-semibold text-foreground-950 mb-2"><i className="ri-bar-chart-2-line text-primary-500 mr-1"></i>成效验收指标</h4>
          <div className="flex flex-wrap gap-1.5">{(bs.success_metrics.content_metrics || []).map((t, i) => chip(t, i, 'bg-blue-50 text-blue-700'))}{(bs.success_metrics.lead_metrics || []).map((t, i) => chip(t, i + 100, 'bg-amber-50 text-amber-700'))}{(bs.success_metrics.private_domain_metrics || []).map((t, i) => chip(t, i + 200, 'bg-purple-50 text-purple-700'))}</div>
        </div>
      )}
    </div>
  );
}

// ===== 复制按钮 =====
function CopyBtn({ text, label = '复制' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  if (!text) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { /* fallback */ const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch { /* ignore */ } document.body.removeChild(ta); }
    setDone(true); setTimeout(() => setDone(false), 1500);
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border border-secondary-200 text-secondary-600 hover:border-primary-300 hover:text-primary-700 cursor-pointer">
      <i className={done ? 'ri-check-line text-green-600' : 'ri-file-copy-line'}></i>{done ? '已复制' : label}
    </button>
  );
}

const PRIORITY_BADGE: Record<string, { label: string; cls: string }> = {
  high: { label: '高', cls: 'bg-red-50 text-red-700' },
  medium: { label: '中', cls: 'bg-amber-50 text-amber-700' },
  low: { label: '低', cls: 'bg-secondary-100 text-secondary-500' },
};

// ===== 角色落地与开发指令 =====
function RolePlaybookSection({ data }: { data: RolePlaybookData }) {
  const rvs = data.role_voice_style;
  const pps = data.platform_profile_setup || {};
  const aop = data.active_outreach_playbook;
  const cp = data.comment_playbook;
  const dm = data.dm_playbook;
  const Block = ({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) => (
    <div className="rounded-lg border border-secondary-200 bg-background-100 p-4">
      <h4 className="text-base sm:text-sm font-semibold text-foreground-900 mb-2.5"><i className={`${icon} text-primary-500 mr-1.5`}></i>{title}</h4>
      {children}
    </div>
  );
  return (
    <div className="space-y-4 pt-3">
      {/* 角色语气 */}
      {rvs && (rvs.personality || rvs.local_tone) && (
        <Block icon="ri-user-voice-line" title="角色语气(所有话术套用)">
          <div className="space-y-1.5 text-[15px] sm:text-[13px] text-foreground-700 leading-relaxed">
            {rvs.personality && <div><span className="text-secondary-400">个性:</span>{rvs.personality}</div>}
            {rvs.background && <div><span className="text-secondary-400">背景:</span>{rvs.background}</div>}
            {rvs.local_tone && <div><span className="text-secondary-400">地方语气:</span>{rvs.local_tone}</div>}
            {rvs.speaking_rules && rvs.speaking_rules.length > 0 && <div><span className="text-secondary-400">说话规则:</span>{rvs.speaking_rules.join('、')}</div>}
            {rvs.avoid_words && rvs.avoid_words.length > 0 && <div className="text-red-500"><span className="text-secondary-400">禁用词:</span>{rvs.avoid_words.join('、')}</div>}
          </div>
        </Block>
      )}

      {/* 平台主页设定 */}
      {Object.keys(pps).length > 0 && (
        <Block icon="ri-home-gear-line" title="平台主页设定">
          <div className="space-y-3">
            {Object.entries(pps).map(([plat, p]) => (
              <div key={plat} className="rounded-lg border border-secondary-200 bg-background-50 p-3">
                <div className="text-sm sm:text-xs font-semibold text-foreground-900 mb-2"><span className="inline-flex items-center px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 text-[11px] sm:text-[10px] mr-1.5">{pLabel(plat)}</span></div>
                {p.recommended_name && (
                  <div className="mb-2">
                    <div className="text-[12px] sm:text-[10px] text-secondary-400 mb-0.5">名字建议(首选)</div>
                    <div className="flex items-center gap-2 flex-wrap"><span className="text-[15px] sm:text-[13px] font-medium text-foreground-900">{p.recommended_name}</span><CopyBtn text={p.recommended_name} label="复制名字" /></div>
                    {p.display_name_options && p.display_name_options.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{p.display_name_options.map((n, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600">{n}</span>)}</div>}
                  </div>
                )}
                {p.bio && (
                  <div className="mb-2">
                    <div className="text-[10px] text-secondary-400 mb-0.5">主页简介</div>
                    <div className="flex items-start gap-2"><div className="flex-1 text-[15px] sm:text-[13px] text-foreground-700 leading-relaxed rounded bg-background-100 border border-secondary-200 p-2.5">{p.bio}</div><CopyBtn text={p.bio} label="复制简介" /></div>
                  </div>
                )}
                {p.profile_tags && p.profile_tags.length > 0 && <div className="mb-2 flex flex-wrap gap-1">{p.profile_tags.map((t, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">#{t}</span>)}</div>}
                {p.homepage_info && p.homepage_info.length > 0 && <div className="mb-2"><div className="text-[10px] text-secondary-400 mb-0.5">主页要放</div><ul className="text-[11px] text-foreground-600 list-decimal ml-4 space-y-0.5">{p.homepage_info.map((h, i) => <li key={i}>{h}</li>)}</ul></div>}
                {p.pinned_content && p.pinned_content.length > 0 && (
                  <div className="mb-2"><div className="text-[10px] text-secondary-400 mb-0.5">置顶内容建议</div>{p.pinned_content.map((pc, i) => <div key={i} className="text-[11px] text-foreground-700"><i className="ri-pushpin-line text-primary-400 mr-1"></i>{pc.title}{pc.purpose && <span className="text-secondary-400">(目的:{pc.purpose})</span>}</div>)}</div>
                )}
                {p.profile_warning && p.profile_warning.length > 0 && <div className="text-[10px] text-red-500"><i className="ri-error-warning-line mr-0.5"></i>禁忌:{p.profile_warning.join('；')}</div>}
              </div>
            ))}
          </div>
        </Block>
      )}

      {/* 主动开发对象 + 去哪找 + 今日照做 */}
      {aop && (
        <Block icon="ri-radar-line" title="本周主动开发">
          {aop.target_user_types && aop.target_user_types.length > 0 && (
            <div className="mb-3"><div className="text-[11px] font-medium text-foreground-700 mb-1">要找的人</div>
              <div className="space-y-1.5">{aop.target_user_types.map((u, i) => (
                <div key={i} className="rounded bg-background-50 border border-secondary-200 p-2">
                  <div className="flex items-center gap-1.5"><span className="text-[12px] font-medium text-foreground-900">{u.type}</span>{u.priority && PRIORITY_BADGE[u.priority] && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${PRIORITY_BADGE[u.priority].cls}`}>优先级 {PRIORITY_BADGE[u.priority].label}</span>}</div>
                  {u.signals && u.signals.length > 0 && <div className="text-[10px] text-secondary-500 mt-0.5">信号:{u.signals.join('、')}</div>}
                </div>
              ))}</div>
            </div>
          )}
          {aop.where_to_find && aop.where_to_find.length > 0 && (
            <div className="mb-3"><div className="text-[11px] font-medium text-foreground-700 mb-1">去哪找</div>
              <div className="space-y-1">{aop.where_to_find.map((w, i) => (
                <div key={i} className="text-[11px] text-foreground-600"><span className="inline-flex items-center px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 text-[10px] mr-1">{pLabel(w.platform || '')}</span>{w.keywords && w.keywords.length > 0 && <span>搜:{w.keywords.map((k) => `「${k}」`).join(' ')}</span>}{w.action && <span className="text-secondary-400"> · {w.action}</span>}</div>
              ))}</div>
            </div>
          )}
          {aop.daily_outreach_tasks && aop.daily_outreach_tasks.length > 0 && (
            <div className="mb-2"><div className="text-[11px] font-medium text-foreground-700 mb-1">今天照做</div><ol className="text-[11px] text-foreground-600 list-decimal ml-4 space-y-0.5">{aop.daily_outreach_tasks.map((t, i) => <li key={i}>{t}</li>)}</ol></div>
          )}
          {aop.do_not_do && aop.do_not_do.length > 0 && <div className="text-[10px] text-red-500"><i className="ri-forbid-line mr-0.5"></i>禁忌:{aop.do_not_do.join('；')}</div>}
        </Block>
      )}

      {/* 留言破冰 */}
      {cp && cp.comment_scenarios && cp.comment_scenarios.length > 0 && (
        <Block icon="ri-chat-1-line" title="留言怎么开口">
          <div className="space-y-2">
            {cp.comment_scenarios.map((s, i) => (
              <div key={i} className="rounded bg-background-50 border border-secondary-200 p-2.5">
                <div className="text-[11px] text-secondary-500 mb-1">情境:{s.scenario}</div>
                <div className="flex items-start gap-2"><div className="flex-1 text-[12px] text-foreground-700 leading-relaxed">{s.reply}</div><CopyBtn text={s.reply || ''} label="复制留言" /></div>
                {s.next_step && <div className="text-[10px] text-secondary-400 mt-1">下一步:{s.next_step}</div>}
              </div>
            ))}
            {cp.comment_rules && cp.comment_rules.length > 0 && <div className="text-[10px] text-secondary-500">规则:{cp.comment_rules.join('；')}</div>}
          </div>
        </Block>
      )}

      {/* 私信破冰 */}
      {dm && (
        <Block icon="ri-mail-send-line" title="私信怎么破冰">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            {dm.when_to_dm && dm.when_to_dm.length > 0 && <div className="rounded bg-green-50/50 border border-green-100 p-2"><div className="text-[10px] font-medium text-green-700 mb-0.5">可以私信</div><ul className="text-[10px] text-foreground-600 list-disc ml-3.5 space-y-0.5">{dm.when_to_dm.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
            {dm.when_not_to_dm && dm.when_not_to_dm.length > 0 && <div className="rounded bg-red-50/50 border border-red-100 p-2"><div className="text-[10px] font-medium text-red-700 mb-0.5">不要私信</div><ul className="text-[10px] text-foreground-600 list-disc ml-3.5 space-y-0.5">{dm.when_not_to_dm.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          </div>
          {dm.dm_openers && dm.dm_openers.length > 0 && (
            <div className="space-y-2 mb-2">{dm.dm_openers.map((o, i) => (
              <div key={i} className="rounded bg-background-50 border border-secondary-200 p-2.5">
                <div className="text-[11px] text-secondary-500 mb-1">情境:{o.scenario}</div>
                <div className="flex items-start gap-2"><div className="flex-1 text-[12px] text-foreground-700 leading-relaxed">{o.message}</div><CopyBtn text={o.message || ''} label="复制私信" /></div>
                {o.follow_up && <div className="text-[10px] text-secondary-400 mt-1">追问:{o.follow_up}</div>}
              </div>
            ))}</div>
          )}
          {dm.lead_flow && dm.lead_flow.length > 0 && <div className="text-[11px] text-foreground-600 mb-1"><span className="text-secondary-400">引流步骤:</span>{dm.lead_flow.join(' → ')}</div>}
          {dm.forbidden_dm && dm.forbidden_dm.length > 0 && <div className="text-[10px] text-red-500"><i className="ri-forbid-line mr-0.5"></i>禁忌:{dm.forbidden_dm.join('；')}</div>}
        </Block>
      )}
    </div>
  );
}
